import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import { DeleteOutlined, DollarOutlined, EditOutlined, PlusOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import AdminLayout from '../components/AdminLayout';
import KhoTongSection from '../components/KhoTongSection';
import PartnerCoffeeManagementSection from '../components/PartnerCoffeeManagementSection';
import { dataEntryUserService } from '../services/dataEntryUserService';
import { coffeeInventoryService } from '../services/coffeeInventoryService';
import { InvoiceNotificationService } from '../services/invoiceNotificationService';
import {
  getMergedInvoiceNotificationsWithStatus,
  getPreferredCustomizations,
  getPreferredMenuDishes,
} from '../services/posMergedDataService';
import {
  NormalizePurchaseItemNamesResult,
  NormalizePurchaseUnitPricesResult,
  NormalizePurchaseUnitsResult,
  purchaseService,
} from '../services/purchaseService';
import { quickButtonService } from '../services/quickButtonService';
import { recipeService } from '../services/recipeService';
import { storeService } from '../services/storeService';
import { supplierService } from '../services/supplierService';
import { warehouseGoodsCatalogService } from '../services/warehouseGoodsCatalogService';
import { warehouseService } from '../services/warehouseService';
import type { WarehouseEntry } from '../types/warehouse';
import { parseJsonToInvoiceNotifications } from '../utils/invoiceJsonUtils';
import { isMassBaseUnit } from '../utils/warehouseGoodsCatalogImportUtils';
import { createRecipeLookup } from '../utils/recipeLookupIndex';
import {
  findMissingOptionAddonWarnings,
  findBestInventoryMatchKey,
  convertIngredientUsageToStockUnit,
  isStockUnitCompatible,
  parseIngredientAmount,
  resolveInvoiceLineIngredients as resolveInvoiceLineIngredientsUtil,
  normalizeIngredientForMatch as normalizeIngredientForMatchUtil,
} from '../utils/ingredientCalculation';
import {
  getPurchaseAmountDelta,
  isInventoryInvoiceCountable,
  isWithinInventoryBalanceCutoff,
  parseInventoryDate,
} from '../utils/inventoryCalculation';
import { CoffeeStockTransaction } from '../types/coffeeInventory';
import { Customization } from '../types/customization';
import { DataEntryUser } from '../types/dataEntryUser';
import { InvoiceNotification, InvoiceSelectedOption } from '../types/invoiceNotification';
import { PurchaseFilterPeriod, PurchaseItem, QuickButtonTemplate } from '../types/purchase';
import { OptionAddon, Recipe, RecipeIngredient } from '../types/recipe';
import { Store } from '../types/store';
import { expenseCatalogService, expenseEntryService } from '../services/expenseCatalogService';
import type { ExpenseCatalogItem } from '../types/expenseCatalog';
import type { Supplier } from '../types/supplier';

const formatNumberVi = (
  value: string | number | undefined | null,
  fractionDigits: number = 0,
  opts?: { useGrouping?: boolean },
) => {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'string') return value;
  if (!Number.isFinite(value)) return '-';

  return new Intl.NumberFormat('vi-VN', {
    useGrouping: opts?.useGrouping ?? true,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

interface PurchaseFormValues {
  code?: string;
  category?: string;
  supplierSource?: string;
  supplierId?: string;
  date: Dayjs;
  itemName: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  storeLocationId?: string;
  warehouseCoffeeId?: string; // Kho tổng: id dòng danh mục hàng hóa (tên field giữ để tương thích dữ liệu cũ)
}

interface QuickRecipeFormValues {
  itemId: string;
  itemName: string;
  group?: string;
  instructions?: string;
  ingredients: RecipeIngredient[];
  optionAddons?: OptionAddonFormValues[];
}

interface InvoiceMenuItem {
  itemId: string;
  itemName: string;
  itemGroup?: string;
}

interface InvoiceOptionItem {
  optionId: string;
  optionName: string;
}

interface OptionAddonFormValues {
  optionId: string;
  optionName: string;
  ingredients: RecipeIngredient[];
}

const recipeGroupOptions = [
  { label: 'Arabica', value: 'Arabica' },
  { label: 'Robusta', value: 'Robusta' },
  { label: 'Cold brew', value: 'Cold brew' },
  { label: 'Non-cafein', value: 'Non-cafein' },
  { label: 'Chai', value: 'Chai' },
  { label: 'Nguyên liệu', value: 'Nguyên liệu' },
];

interface PurchaseSummaryRow {
  key: string;
  ingredientName: string;
  variantNames: string[];
  unit: string;
  entryCount: number;
  totalQuantity: number;
  totalAmount: number;
}

interface IngredientInventoryRow {
  key: string;
  ingredientName: string;
  storeLocationId?: string;
  storeName: string;
  unit: string;
  importCount: number;
  importedQuantity: number;
  importedAmount: number;
  consumedQuantity: number;
  stockQuantity: number;
  averageImportPrice: number;
  estimatedStockAmount: number;
  matchedBy?: 'same_store' | 'fallback_new';
  sameStoreMatchCount?: number;
  fallbackMissCount?: number;
  importDetails: IngredientInventoryImportDetail[];
  consumptionDetails: IngredientInventoryConsumeDetail[];
}

interface IngredientInventoryImportDetail {
  key: string;
  date: string;
  storeName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  createdBy: string;
  itemName: string;
}

interface IngredientInventoryConsumeDetail {
  key: string;
  invoiceCode: string;
  orderCode: string;
  checkInDate: string;
  checkInTime: string;
  store: string;
  itemName: string;
  usedAmount: number;
  formula: string;
  source: string;
}

const filterOptions: Array<{ label: string; value: PurchaseFilterPeriod }> = [
  { label: 'Ngày', value: 'day' },
  { label: 'Tuần', value: 'week' },
  { label: 'Tháng', value: 'month' },
  { label: 'Năm', value: 'year' },
  { label: 'Tất cả', value: 'all' },
];

type TemperatureMode = 'hot' | 'iced';

interface IngredientOutputRow {
  key: string;
  ingredientName: string;
  unit: string;
  totalAmount: number;
  invoiceCount: number;
}

interface RecipeMissRow {
  key: string;
  itemId: string;
  itemName: string;
  count: number;
  quantity: number;
}

interface InvoiceGroupRow {
  key: string;
  invoiceCode: string;
  orderCode: string;
  checkInDate: string;
  checkInTime: string;
  store: string;
  itemNames: string[];
  bonusOptionNames: string[];
  lineCount: number;
  totalItemQuantity: number;
  totalAmount: number;
  rows: InvoiceNotification[];
}

interface InvoiceLineFormulaRow {
  key: string;
  itemName: string;
  itemId: string;
  quantity: number;
  mode: string;
  bonusOptions: string;
  recipeStatus: string;
  totalUsedAmount: number;
}

interface InvoiceIngredientFormulaRow {
  key: string;
  itemName: string;
  ingredientName: string;
  source: string;
  formula: string;
  usedAmount: number;
  unit: string;
}

interface LineIngredientUsage {
  ingredientKey: string;
  ingredientName: string;
  usedAmount: number;
  unit: string;
  source: string;
  formula: string;
}

/** Upload JSON iPOS legacy (không dùng cho CUKCUK — CUKCUK vào bảng riêng). */
const invoiceNotificationService = new InvoiceNotificationService();

const normalizeText = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

interface NormalizedPurchaseMeasurement {
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  converted: boolean;
  inputUnit: string;
}

const roundNumber = (value: number, precision: number = 6) => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const normalizePurchaseMeasurement = (
  quantityRaw: number,
  unitRaw: string,
  unitPriceRaw: number,
): NormalizedPurchaseMeasurement => {
  const quantity = Number(quantityRaw || 0);
  const unitPrice = Number(unitPriceRaw || 0);
  const inputUnit = String(unitRaw || '').trim();
  const unitKey = normalizeText(inputUnit).replace(/\s+/g, '').replace(/\./g, '');

  const kgUnits = new Set(['kg', 'kilo', 'kilogram']);
  const gUnits = new Set(['g', 'gram', 'gam']);
  const literUnits = new Set(['l', 'lit', 'liter', 'litre']);
  const mlUnits = new Set(['ml', 'mililit', 'milliliter', 'millilitre']);

  let normalizedQuantity = quantity;
  let normalizedUnitPrice = unitPrice;
  let normalizedUnit = inputUnit;
  let converted = false;

  if (kgUnits.has(unitKey)) {
    normalizedQuantity = quantity * 1000;
    normalizedUnitPrice = unitPrice / 1000;
    normalizedUnit = 'g';
    converted = true;
  } else if (gUnits.has(unitKey)) {
    normalizedUnit = 'g';
    converted = inputUnit !== 'g';
  } else if (literUnits.has(unitKey)) {
    normalizedQuantity = quantity * 1000;
    normalizedUnitPrice = unitPrice / 1000;
    normalizedUnit = 'g';
    converted = true;
  } else if (mlUnits.has(unitKey)) {
    normalizedUnit = 'g';
    converted = inputUnit !== 'g';
  }

  const finalQuantity = roundNumber(normalizedQuantity);
  const finalUnitPrice = roundNumber(normalizedUnitPrice);
  const finalTotal = roundNumber(finalQuantity * finalUnitPrice);

  return {
    quantity: finalQuantity,
    unit: normalizedUnit,
    unitPrice: finalUnitPrice,
    total: finalTotal,
    converted,
    inputUnit,
  };
};

/** Ghi xuất kho tổng: phiếu nhập quy về g + hàng khối lượng → công thức đ/kg; còn lại theo đơn vị phiếu. */
const buildWarehouseExportMeasurement = (
  normalized: NormalizedPurchaseMeasurement,
  good: { isMass: boolean } | undefined,
) => {
  if (good?.isMass && normalized.unit === 'g') {
    return {
      quantity: normalized.quantity,
      unitPrice: normalized.unitPrice * 1000,
      unit: 'g',
      amountMode: 'per1000g' as const,
    };
  }
  return {
    quantity: normalized.quantity,
    unitPrice: normalized.unitPrice,
    unit: normalized.unit,
    amountMode: 'baseUnit' as const,
  };
};

const isInPeriod = (invoiceDate: Dayjs, period: PurchaseFilterPeriod, selectedDate: Dayjs) => {
  switch (period) {
    case 'day':
      return invoiceDate.isSame(selectedDate, 'day');
    case 'week':
      return invoiceDate.isSame(selectedDate, 'week');
    case 'month':
      return invoiceDate.isSame(selectedDate, 'month');
    case 'year':
      return invoiceDate.isSame(selectedDate, 'year');
    default:
      return true;
  }
};

const normalizeIngredientForMatch = (value?: string): string => normalizeIngredientForMatchUtil(value);


const resolveOptionPrice = (option: InvoiceSelectedOption): number => {
  const directPrice = Number(option.price);
  if (!Number.isNaN(directPrice) && directPrice > 0) return directPrice;
  const amount = Number(option.amount);
  const quantity = Number(option.quantity || 0);
  if (!Number.isNaN(amount) && quantity > 0) return amount / quantity;
  if (!Number.isNaN(amount)) return amount;
  return 0;
};

const formatBonusOptions = (options: InvoiceSelectedOption[]): string[] => {
  const bonusMap = new Map<string, number>();
  options.forEach((option) => {
    const optionName = (option.optionName || option.optionId || '').trim();
    if (!optionName) return;
    const optionPrice = resolveOptionPrice(option);
    if (optionPrice <= 0) return;
    const optionQty = Number(option.quantity || 0);
    const current = bonusMap.get(optionName) || 0;
    bonusMap.set(optionName, current + (optionQty > 0 ? optionQty : 1));
  });
  return Array.from(bonusMap.entries()).map(([name, qty]) => `${name} x${qty}`);
};

const resolveInvoiceLineIngredients = (
  invoice: InvoiceNotification,
  recipe?: Recipe | null,
): { modeLabel: string; lineItems: LineIngredientUsage[]; recipeStatus: string } =>
  resolveInvoiceLineIngredientsUtil(invoice, recipe);

const getCurrentUserId = () => {
  const possibleKeys = ['dataEntryUserId', 'userId', 'localId', 'zaloUserId'];
  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value && value.trim()) {
      return value.trim();
    }
  }
  return 'admin8amcoffee';
};

interface IngredientManagementProps {
  embedded?: boolean;
  initialTabKey?: string;
  allowedTabKeys?: string[];
  hideNormalizationActions?: boolean;
}

const IngredientManagement: React.FC<IngredientManagementProps> = ({
  embedded = false,
  initialTabKey = 'kho-tong',
  allowedTabKeys,
  hideNormalizationActions = false,
}) => {
  const [form] = Form.useForm<PurchaseFormValues>();
  const [quickRecipeForm] = Form.useForm<QuickRecipeFormValues>();
  const [transferForm] = Form.useForm<{
    date: Dayjs;
    toStoreId: string;
    quantity: number;
    notes?: string;
  }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [normalizingNames, setNormalizingNames] = useState(false);
  const [normalizingUnits, setNormalizingUnits] = useState(false);
  const [normalizingUnitPrices, setNormalizingUnitPrices] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PurchaseItem | null>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSourceItem, setTransferSourceItem] = useState<PurchaseItem | null>(null);

  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([]);
  const [allPurchaseItems, setAllPurchaseItems] = useState<PurchaseItem[]>([]);
  const [quickButtons, setQuickButtons] = useState<QuickButtonTemplate[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierForm] = Form.useForm<{ code: string; name: string; phone?: string; address?: string; note?: string }>();
  const [users, setUsers] = useState<DataEntryUser[]>([]);
  const [currentUser, setCurrentUser] = useState<DataEntryUser | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState<PurchaseFilterPeriod>('month');
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [storeFilter, setStoreFilter] = useState<string>('all');

  const [outputDataLoaded, setOutputDataLoaded] = useState(false);
  const [allPurchaseItemsLoaded, setAllPurchaseItemsLoaded] = useState(false);
  const [outputLoading, setOutputLoading] = useState(false);
  const [outputPeriodFilter, setOutputPeriodFilter] = useState<PurchaseFilterPeriod>('month');
  const [outputSelectedDate, setOutputSelectedDate] = useState<Dayjs>(dayjs());
  const [outputStoreFilter, setOutputStoreFilter] = useState<string>('all');
  const [outputSearchTerm, setOutputSearchTerm] = useState('');
  const [ingredientSearchTerm, setIngredientSearchTerm] = useState('');
  const [invoiceItemSearchTerm, setInvoiceItemSearchTerm] = useState('');
  const [invoiceRows, setInvoiceRows] = useState<InvoiceNotification[]>([]);
  const [invoiceSourceWarnings, setInvoiceSourceWarnings] = useState<string[]>([]);
  const [coffeeTransactions, setCoffeeTransactions] = useState<CoffeeStockTransaction[]>([]);
  const [coffeeTransactionsLoaded, setCoffeeTransactionsLoaded] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [invoiceTablePage, setInvoiceTablePage] = useState(1);
  const [invoiceTablePageSize, setInvoiceTablePageSize] = useState(8);
  const [formulaModalOpen, setFormulaModalOpen] = useState(false);
  const [uploadingInvoiceJson, setUploadingInvoiceJson] = useState(false);
  const [selectedInvoiceGroup, setSelectedInvoiceGroup] = useState<InvoiceGroupRow | null>(null);
  const [missingRecipeModalOpen, setMissingRecipeModalOpen] = useState(false);
  const [savingMissingRecipe, setSavingMissingRecipe] = useState(false);
  const [selectedMissingRecipe, setSelectedMissingRecipe] = useState<RecipeMissRow | null>(null);
  const [quickPasteText, setQuickPasteText] = useState('');
  const [menuItems, setMenuItems] = useState<InvoiceMenuItem[]>([]);
  const [customizations, setCustomizations] = useState<Customization[]>([]);

  const [inventoryPeriodFilter, setInventoryPeriodFilter] = useState<PurchaseFilterPeriod>('month');
  const [inventorySelectedDate, setInventorySelectedDate] = useState<Dayjs>(dayjs());
  const [inventoryStoreFilter, setInventoryStoreFilter] = useState<string>('all');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [inventoryDetailModalOpen, setInventoryDetailModalOpen] = useState(false);
  const [selectedInventoryRow, setSelectedInventoryRow] = useState<IngredientInventoryRow | null>(null);

  // Kho tổng linkage — dùng cho modal Nguyên liệu nhập
  const [warehouseGoods, setWarehouseGoods] = useState<
    { id: string; name: string; productCode: string; baseUnit: string; isMass: boolean }[]
  >([]);
  const [warehouseEntries, setWarehouseEntries] = useState<WarehouseEntry[]>([]);
  const [fromWarehouse, setFromWarehouse] = useState(false);

  /** Tăng khi chọn lại tab → KhoTongSection / PartnerCoffee tự loadData. */
  const [khoTongReloadToken, setKhoTongReloadToken] = useState(0);
  const [partnerTabReloadToken, setPartnerTabReloadToken] = useState(0);

  // Expense entry state (nhập chi phí từ tab Nguyên liệu nhập)
  const [expenseEntryModalOpen, setExpenseEntryModalOpen] = useState(false);
  const [expenseCatalog, setExpenseCatalog] = useState<ExpenseCatalogItem[]>([]);
  const [expenseEntryForm] = Form.useForm<{ date: Dayjs; expenseCatalogId: string; unitPrice: number; notes?: string }>();
  const [expenseSaving, setExpenseSaving] = useState(false);
  const watchedExpenseCatalogId = Form.useWatch('expenseCatalogId', expenseEntryForm);
  const expenseCatalogOptions = useMemo(
    () => expenseCatalog.map((c) => ({ value: c.id || '', label: c.name, defaultUnitPrice: c.defaultUnitPrice || 0 })),
    [expenseCatalog],
  );
  const expenseSelectedOpt = expenseCatalogOptions.find((o) => o.value === watchedExpenseCatalogId);

  const isMountedRef = useRef(true);
  const purchaseListCacheRef = useRef<Map<string, PurchaseItem[]>>(new Map());
  const purchaseListInFlightRef = useRef<Map<string, Promise<PurchaseItem[]>>>(new Map());
  const purchaseListRequestIdRef = useRef(0);
  const allPurchaseInFlightRef = useRef<Promise<PurchaseItem[]> | null>(null);
  const allPurchaseRequestIdRef = useRef(0);
  const outputDataInFlightRef = useRef<Promise<void> | null>(null);
  const outputDataRequestIdRef = useRef(0);
  const coffeeTransactionsInFlightRef = useRef<Promise<void> | null>(null);
  const warehouseGoodsInFlightRef = useRef<Promise<void> | null>(null);
  const warehouseGoodsLoadedRef = useRef(false);
  const warehouseEntriesInFlightRef = useRef<Promise<void> | null>(null);
  const warehouseEntriesLoadedRef = useRef(false);

  const selectedQuickRecipeItemId = Form.useWatch('itemId', quickRecipeForm);
  const watchedCategory = Form.useWatch('category', form);
  // "cà phê", "cafe", "ca phe"... đều khớp sau khi normalize bỏ dấu
  const isCoffeeCategory = normalizeText(watchedCategory || '').includes('ca phe') ||
    normalizeText(watchedCategory || '').includes('cafe');
  const selectedQuickRecipeGroup = Form.useWatch('group', quickRecipeForm);
  const normalizeCode = (value?: string) => (value || '').trim().toLowerCase();
  const isQuickSingleAmountMode = normalizeCode(selectedQuickRecipeGroup) === 'chai';

  const extractOptionsFromCustomizations = (items: Customization[]): InvoiceOptionItem[] => {
    const seen = new Map<string, string>();
    items.forEach((customization) => {
      (customization.groups || []).forEach((group) => {
        (group.dishCodes || []).forEach((dish) => {
          const optionId = dish.code?.trim();
          const optionName = dish.name?.trim();
          if (!optionId || !optionName) return;
          if (dish.isActive === false) return;
          if (!seen.has(optionId)) seen.set(optionId, optionName);
        });
      });
    });

    return Array.from(seen.entries())
      .map(([optionId, optionName]) => ({ optionId, optionName }))
      .sort((a, b) => a.optionName.localeCompare(b.optionName, 'vi'));
  };

  const allCustomizationOptions = useMemo(
    () => extractOptionsFromCustomizations(customizations),
    [customizations]
  );

  const currentDishOptions = useMemo(() => {
    if (!selectedQuickRecipeItemId) return [];
    const selectedCode = normalizeCode(selectedQuickRecipeItemId);
    const matched = customizations.filter((customization) =>
      (customization.dishCode || []).some((dishCode) => normalizeCode(dishCode) === selectedCode)
    );
    return extractOptionsFromCustomizations(matched);
  }, [customizations, selectedQuickRecipeItemId]);

  const selectableOptionItems = currentDishOptions.length
    ? currentDishOptions
    : allCustomizationOptions;

  const parseQuickPasteIngredients = (raw: string, singleAmountMode: boolean): RecipeIngredient[] => {
    const rows = raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return rows
      .map((line) => {
        const tabParts = line
          .split('\t')
          .map((part) => part.trim())
          .filter(Boolean);

        if (tabParts.length >= 3) {
          const [name, hotAmount, icedAmount] = tabParts;
          return { name, hotAmount: hotAmount || 'x', icedAmount: icedAmount || 'x' };
        }

        if (singleAmountMode && tabParts.length >= 2) {
          const [name, amount] = tabParts;
          return { name, hotAmount: amount || 'x', icedAmount: amount || 'x' };
        }

        const compactParts = line
          .split(/\s{2,}|\s*\|\s*|\s*;\s*|\s*,\s*/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (compactParts.length >= 3) {
          const [name, hotAmount, icedAmount] = compactParts;
          return { name, hotAmount: hotAmount || 'x', icedAmount: icedAmount || 'x' };
        }

        if (singleAmountMode && compactParts.length >= 2) {
          const [name, amount] = compactParts;
          return { name, hotAmount: amount || 'x', icedAmount: amount || 'x' };
        }

        return null;
      })
      .filter((item): item is RecipeIngredient => !!item && !!item.name);
  };

  const getPurchaseCacheKey = useCallback((period: PurchaseFilterPeriod, date: Dayjs) => {
    switch (period) {
      case 'day':
        return `${period}:${date.format('YYYY-MM-DD')}`;
      case 'week':
        return `${period}:${date.startOf('week').format('YYYY-MM-DD')}`;
      case 'month':
        return `${period}:${date.format('YYYY-MM')}`;
      case 'year':
        return `${period}:${date.format('YYYY')}`;
      default:
        return 'all';
    }
  }, []);

  const loadCurrentUser = async () => {
    const fallbackUser: DataEntryUser = {
      userId: getCurrentUserId(),
      name: 'Admin 8AM',
      avatar: '',
      isAdmin: true,
      dataEntryRole: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      const user = await dataEntryUserService.getUserByUserId(getCurrentUserId());
      if (!isMountedRef.current) return;
      setCurrentUser(user || fallbackUser);
    } catch (error) {
      if (!isMountedRef.current) return;
      setCurrentUser(fallbackUser);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await dataEntryUserService.getAllUsers();
      if (!isMountedRef.current) return;
      setUsers(data);
    } catch (error) {
      if (!isMountedRef.current) return;
      setUsers([]);
    }
  };

  const loadStores = async () => {
    try {
      const data = await storeService.getStores();
      if (!isMountedRef.current) return;
      setStores(data || []);
    } catch (error) {
      if (!isMountedRef.current) return;
      setStores([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getActiveSuppliers();
      if (isMountedRef.current) setSuppliers(data);
    } catch {
      if (isMountedRef.current) setSuppliers([]);
    }
  };

  const createSupplier = async () => {
    try {
      const values = await supplierForm.validateFields();
      setSupplierSaving(true);
      const supplier = await supplierService.createSupplier(values);
      setSuppliers((current) => [...current, supplier].sort((a, b) => a.name.localeCompare(b.name, 'vi')));
      form.setFieldsValue({ supplierId: supplier.id, supplierSource: supplier.name });
      setSupplierModalOpen(false);
      supplierForm.resetFields();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSupplierSaving(false);
    }
  };

  const loadQuickButtons = async () => {
    try {
      const data = await quickButtonService.getAllQuickButtons();
      if (!isMountedRef.current) return;
      setQuickButtons(data || []);
    } catch (error) {
      if (!isMountedRef.current) return;
      setQuickButtons([]);
    }
  };

  const loadWarehouseGoodsCatalog = useCallback(async (force = false) => {
    if (!force && warehouseGoodsLoadedRef.current) {
      if (warehouseGoodsInFlightRef.current) {
        await warehouseGoodsInFlightRef.current;
      }
      return;
    }

    if (warehouseGoodsInFlightRef.current) {
      await warehouseGoodsInFlightRef.current;
    }

    const request = (async () => {
      try {
        const data = await warehouseGoodsCatalogService.getAll();
        if (!isMountedRef.current) return;
        warehouseGoodsLoadedRef.current = (data || []).length > 0;
        setWarehouseGoods(
          (data || []).map((g) => ({
            id: g.id || '',
            name: g.name,
            productCode: g.productCode || '',
            baseUnit: (g.baseUnit || 'g').trim(),
            isMass: isMassBaseUnit(g.baseUnit || 'g'),
          })),
        );
      } catch {
        // silently ignore — not critical
      }
    })();

    warehouseGoodsInFlightRef.current = request;
    try {
      await request;
    } finally {
      if (warehouseGoodsInFlightRef.current === request) {
        warehouseGoodsInFlightRef.current = null;
      }
    }
  }, []);

  const loadWarehouseEntries = useCallback(async (force = false) => {
    if (!force && warehouseEntriesLoadedRef.current) {
      if (warehouseEntriesInFlightRef.current) {
        await warehouseEntriesInFlightRef.current;
      }
      return;
    }

    if (warehouseEntriesInFlightRef.current) {
      await warehouseEntriesInFlightRef.current;
    }

    const request = (async () => {
      try {
        const data = await warehouseService.getAllEntries();
        if (!isMountedRef.current) return;
        warehouseEntriesLoadedRef.current = (data || []).length > 0;
        setWarehouseEntries(data || []);
      } catch {
        // silently ignore — not critical
      }
    })();

    warehouseEntriesInFlightRef.current = request;
    try {
      await request;
    } finally {
      if (warehouseEntriesInFlightRef.current === request) {
        warehouseEntriesInFlightRef.current = null;
      }
    }
  }, []);

  const loadMenuItems = async () => {
    try {
      const { dishes } = await getPreferredMenuDishes();
      if (!isMountedRef.current) return;
      setMenuItems(
        dishes
          .filter((dish) => !!dish.code && !!dish.name && !dish.isVirtualDish)
          .map((dish) => ({
            itemId: dish.code.trim(),
            itemName: dish.name.trim(),
            itemGroup: (dish.groupName || dish.group || '').trim(),
          }))
          .sort((a, b) => a.itemName.localeCompare(b.itemName, 'vi'))
      );
    } catch (error) {
      if (!isMountedRef.current) return;
      setMenuItems([]);
      message.warning('Không thể tải danh sách món để hỗ trợ tạo công thức nhanh.');
    }
  };

  const loadCustomizations = async () => {
    try {
      const data = await getPreferredCustomizations('cukcuk');
      if (!isMountedRef.current) return;
      setCustomizations(data || []);
    } catch (error) {
      if (!isMountedRef.current) return;
      setCustomizations([]);
      message.warning('Không thể tải danh sách tùy chọn để hỗ trợ tạo công thức nhanh.');
    }
  };

  const loadIngredientOutputData = async (force = false) => {
    if (!force && outputDataLoaded) return;
    if (!force && outputDataInFlightRef.current) {
      await outputDataInFlightRef.current;
      return;
    }

    const requestId = ++outputDataRequestIdRef.current;
    setOutputLoading(true);

    const request = (async () => {
      try {
        const [invoiceResult, allRecipes] = await Promise.all([
          getMergedInvoiceNotificationsWithStatus(),
          recipeService.getAllRecipes(),
        ]);

        if (!isMountedRef.current || requestId !== outputDataRequestIdRef.current) return;

        setInvoiceRows(invoiceResult.rows || []);
        setInvoiceSourceWarnings(invoiceResult.warnings);
        setRecipes(allRecipes || []);
        setOutputDataLoaded(true);
      } catch (error) {
        if (!isMountedRef.current || requestId !== outputDataRequestIdRef.current) return;
        message.error('Không thể tải dữ liệu hóa đơn hoặc công thức để tính nguyên liệu sử dụng.');
        setInvoiceRows([]);
        setInvoiceSourceWarnings([]);
        setRecipes([]);
      } finally {
        if (isMountedRef.current && requestId === outputDataRequestIdRef.current) {
          setOutputLoading(false);
        }
      }
    })();

    outputDataInFlightRef.current = request;
    try {
      await request;
    } finally {
      if (outputDataInFlightRef.current === request) {
        outputDataInFlightRef.current = null;
      }
    }
  };

  const loadCoffeeTransactions = async (force = false) => {
    if (!force && coffeeTransactionsLoaded) return;
    if (!force && coffeeTransactionsInFlightRef.current) {
      await coffeeTransactionsInFlightRef.current;
      return;
    }

    const request = (async () => {
      try {
        const transactions = await coffeeInventoryService.getAllTransactions();
        if (!isMountedRef.current) return;
        setCoffeeTransactions(transactions || []);
        setCoffeeTransactionsLoaded(true);
      } catch {
        if (!isMountedRef.current) return;
        setCoffeeTransactions([]);
        message.error('Không thể tải giao dịch cà phê để tính nguyên liệu tồn.');
      }
    })();

    coffeeTransactionsInFlightRef.current = request;
    try {
      await request;
    } finally {
      if (coffeeTransactionsInFlightRef.current === request) {
        coffeeTransactionsInFlightRef.current = null;
      }
    }
  };

  const loadPurchaseItems = useCallback(async (period: PurchaseFilterPeriod, date: Dayjs, force = false) => {
    const cacheKey = getPurchaseCacheKey(period, date);
    if (!force) {
      const cached = purchaseListCacheRef.current.get(cacheKey);
      if (cached) {
        setPurchaseItems(cached);
        return;
      }

      const inFlight = purchaseListInFlightRef.current.get(cacheKey);
      if (inFlight) {
        await inFlight;
        return;
      }
    }

    const requestId = ++purchaseListRequestIdRef.current;
    setLoading(true);

    const request = (async (): Promise<PurchaseItem[]> => {
      const jsDate = date.toDate();

      switch (period) {
        case 'day':
          return purchaseService.getPurchaseItemsByDate(jsDate);
        case 'week':
          return purchaseService.getPurchaseItemsByWeek(jsDate);
        case 'month':
          return purchaseService.getPurchaseItemsByMonth(jsDate);
        case 'year':
          return purchaseService.getPurchaseItemsByYear(jsDate);
        default:
          return purchaseService.getAllPurchaseItems();
      }
    })();

    purchaseListInFlightRef.current.set(cacheKey, request);

    try {
      const data = await request;
      if (!isMountedRef.current || requestId !== purchaseListRequestIdRef.current) return;
      purchaseListCacheRef.current.set(cacheKey, data || []);
      setPurchaseItems(data || []);
    } catch (error) {
      if (!isMountedRef.current || requestId !== purchaseListRequestIdRef.current) return;
      message.error('Không thể tải danh sách nguyên liệu nhập.');
    } finally {
      purchaseListInFlightRef.current.delete(cacheKey);
      if (isMountedRef.current && requestId === purchaseListRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [getPurchaseCacheKey]);

  const loadAllPurchaseItems = async (force = false) => {
    if (!force && allPurchaseItemsLoaded) return;
    if (!force && allPurchaseInFlightRef.current) {
      await allPurchaseInFlightRef.current;
      return;
    }

    const requestId = ++allPurchaseRequestIdRef.current;
    const request = purchaseService.getAllPurchaseItems();
    allPurchaseInFlightRef.current = request;

    try {
      const data = await request;
      if (!isMountedRef.current || requestId !== allPurchaseRequestIdRef.current) return;
      setAllPurchaseItems(data || []);
      setAllPurchaseItemsLoaded(true);
    } catch (error) {
      if (!isMountedRef.current || requestId !== allPurchaseRequestIdRef.current) return;
      setAllPurchaseItems([]);
    } finally {
      if (allPurchaseInFlightRef.current === request) {
        allPurchaseInFlightRef.current = null;
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    loadCurrentUser();
    loadUsers();
    loadStores();
    loadSuppliers();
    loadQuickButtons();
    loadMenuItems();
    loadCustomizations();
    // loadIngredientOutputData và loadAllPurchaseItems được lazy load theo tab

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadPurchaseItems(periodFilter, selectedDate);
  }, [periodFilter, selectedDate, loadPurchaseItems]);

  useEffect(() => {
    if (!modalOpen) return;
    loadWarehouseGoodsCatalog(true);
    loadWarehouseEntries(true);
  }, [modalOpen, loadWarehouseGoodsCatalog, loadWarehouseEntries]);

  // Phiếu cũ thiếu warehouseCoffeeId: gán lại theo tên khi đã tải danh mục
  useEffect(() => {
    if (!modalOpen || !editingItem || warehouseGoods.length === 0) return;
    const wid = (editingItem.warehouseCoffeeId || '').trim();
    if (wid && warehouseGoods.some((g) => g.id === wid)) return;
    const name = (editingItem.itemName || '').trim();
    if (!name) return;
    const m = warehouseGoods.find((g) => normalizeText(g.name) === normalizeText(name));
    if (m?.id) {
      form.setFieldsValue({ warehouseCoffeeId: m.id, itemName: m.name });
    }
  }, [modalOpen, editingItem, warehouseGoods, form]);

  // Khi category thay đổi sang "cà phê": tự bật fromWarehouse + reset chọn hàng
  useEffect(() => {
    if (!modalOpen) return;
    if (isCoffeeCategory) {
      setFromWarehouse(true);
      form.setFieldsValue({ itemName: undefined, warehouseCoffeeId: undefined });
    } else {
      setFromWarehouse(false);
      form.setFieldsValue({ itemName: undefined, warehouseCoffeeId: undefined });
    }
  }, [form, isCoffeeCategory, modalOpen]);

  const canAccessDataEntry = Boolean(currentUser?.isAdmin || currentUser?.dataEntryRole);
  const canEditPurchase = Boolean(currentUser?.isAdmin);

  const getStoreUidValue = useCallback((store: Store): string | undefined => {
    return ((store as any).store_uid || (store as any).storeUid || '').toString().trim() || undefined;
  }, []);

  const getStoreOptionValue = useCallback((store: Store): string => {
    return getStoreUidValue(store) || (store.id || '');
  }, [getStoreUidValue]);

  const storeNameMap = useMemo(() => {
    return stores.reduce((acc, store) => {
      if (store.id) {
        acc[store.id] = store.name;
      }

      const storeUidValue = getStoreUidValue(store);
      if (storeUidValue) {
        acc[storeUidValue] = store.name;
      }

      return acc;
    }, {} as Record<string, string>);
  }, [stores, getStoreUidValue]);

  const storeIdByAlias = useMemo(() => {
    const normalize = (value?: string) => normalizeText(value).replace(/\s+/g, ' ').trim();
    const idToCanonical = new Map<string, string>();

    stores.forEach((store) => {
      const docId = (store.id || '').trim();
      const uid = getStoreUidValue(store) || '';
      const canonical = uid || docId;
      if (!canonical) return;

      if (docId) idToCanonical.set(docId, canonical);
      if (uid) idToCanonical.set(uid, canonical);
    });

    const byName = stores
      .filter((store) => !!store.id || !!getStoreUidValue(store))
      .map((store) => ({
        id: getStoreOptionValue(store),
        normalizedName: normalize(store.name),
      }));

    const findByKeyword = (keyword: string) => {
      const normalizedKeyword = normalize(keyword);
      const found = byName.find(
        (item) => item.normalizedName.includes(normalizedKeyword) || normalizedKeyword.includes(item.normalizedName)
      );
      return found?.id;
    };

    return {
      tangBatHo: findByKeyword('tang bat ho'),
      xuanThuy241: findByKeyword('241 xuan thuy'),
      byName,
      idToCanonical,
      byId: new Set(
        stores
          .flatMap((store) => [store.id, getStoreUidValue(store)])
          .filter((value): value is string => Boolean(value))
      ),
    };
  }, [stores, getStoreOptionValue, getStoreUidValue]);

  const resolveStoreIdFromRawValue = useCallback((storeValue?: string): string | undefined => {
    const raw = (storeValue || '').trim();
    if (!raw) return undefined;

    const normalizedRaw = normalizeText(raw).replace(/\s+/g, ' ').trim();
    if (!normalizedRaw || normalizedRaw === '-' || normalizedRaw === 'unknown' || normalizedRaw === 'null') {
      return undefined;
    }

    if (storeIdByAlias.byId.has(raw)) return storeIdByAlias.idToCanonical.get(raw) || raw;

    const direct = storeIdByAlias.byName.find(
      (item) =>
        item.normalizedName === normalizedRaw ||
        item.normalizedName.includes(normalizedRaw) ||
        normalizedRaw.includes(item.normalizedName)
    );
    return direct?.id;
  }, [storeIdByAlias]);

  const resolveStoreIdFromInvoice = useCallback((invoice: InvoiceNotification): string | undefined => {
    const fromStoreUid = resolveStoreIdFromRawValue(invoice.storeUid || (invoice as any)?.store_uid);
    if (fromStoreUid) return fromStoreUid;

    return resolveStoreIdFromRawValue(invoice.store);
  }, [resolveStoreIdFromRawValue]);

  const resolveStoreDisplayNameFromInvoice = useCallback((invoice: InvoiceNotification): string => {
    const storeId = resolveStoreIdFromInvoice(invoice);
    if (storeId && storeNameMap[storeId]) return storeNameMap[storeId];

    const raw = (invoice.store || '').trim();
    if (raw && !storeIdByAlias.byId.has(raw)) return raw;

    return storeId ? (storeNameMap[storeId] || storeId) : '-';
  }, [resolveStoreIdFromInvoice, storeNameMap, storeIdByAlias]);

  const resolveStoreIdForPurchaseItem = useCallback((item: PurchaseItem): string | undefined => {
    const directStoreId = resolveStoreIdFromRawValue(item.storeLocationId);
    if (directStoreId) return directStoreId;

    const fromNameText = resolveStoreIdFromRawValue((item as any).storeLocationName);
    if (fromNameText) return fromNameText;

    const createdBy = normalizeText(item.createdBy);
    if (!createdBy) return undefined;

    if (createdBy.includes('hiep')) return storeIdByAlias.tangBatHo;
    if (createdBy.includes('linh nguyen') || createdBy.includes('linhnguyen')) return storeIdByAlias.xuanThuy241;

    return undefined;
  }, [resolveStoreIdFromRawValue, storeIdByAlias]);

  const filteredItems = useMemo(() => {
    return purchaseItems.filter((item) => {
      const effectiveStoreId = resolveStoreIdForPurchaseItem(item);
      const matchStore = storeFilter === 'all' || effectiveStoreId === storeFilter;
      const keyword = searchTerm.trim().toLowerCase();
      const matchSearch =
        !keyword ||
        item.itemName.toLowerCase().includes(keyword) ||
        (item.code || '').toLowerCase().includes(keyword) ||
        (item.category || '').toLowerCase().includes(keyword) ||
        (item.supplierSource || '').toLowerCase().includes(keyword);

      return matchStore && matchSearch;
    });
  }, [purchaseItems, searchTerm, storeFilter, resolveStoreIdForPurchaseItem]);

  const totalOrders = filteredItems.length;
  const totalAmount = filteredItems.reduce((sum, item) => sum + (item.total || 0), 0);

  const purchaseSummaryRows = useMemo(() => {
    const grouped = new Map<string, PurchaseSummaryRow>();

    filteredItems.forEach((item) => {
      const normalizedName = normalizeText(item.itemName);
      if (!normalizedName) return;

      const unitKey = normalizeText(item.unit || '');
      const key = `${normalizedName}__${unitKey || '-'}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.entryCount += 1;
        existing.totalQuantity += Number(item.quantity || 0);
        existing.totalAmount += Number(item.total || 0);
        if (item.itemName && !existing.variantNames.includes(item.itemName)) {
          existing.variantNames.push(item.itemName);
        }
      } else {
        grouped.set(key, {
          key,
          ingredientName: item.itemName,
          variantNames: item.itemName ? [item.itemName] : [],
          unit: item.unit || '-',
          entryCount: 1,
          totalQuantity: Number(item.quantity || 0),
          totalAmount: Number(item.total || 0),
        });
      }
    });

    return Array.from(grouped.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredItems]);

  const purchaseCategoryOptions = useMemo(() => {
    const categoryMap = new Map<string, string>();

    categoryMap.set(normalizeText('Hạt cà phê'), 'Hạt cà phê');

    allPurchaseItems.forEach((item) => {
      const category = (item.category || '').trim();
      const key = normalizeText(category);
      if (!key || categoryMap.has(key)) return;
      categoryMap.set(key, category);
    });

    quickButtons.forEach((button) => {
      const category = (button.category || '').trim();
      const key = normalizeText(category);
      if (!key || categoryMap.has(key)) return;
      categoryMap.set(key, category);
    });

    const firstCategory = categoryMap.get(normalizeText('Hạt cà phê'));
    const remainingCategories = Array.from(categoryMap.values())
      .filter((category) => normalizeText(category) !== normalizeText('Hạt cà phê'))
      .sort((a, b) => a.localeCompare(b, 'vi'));

    const orderedCategories = firstCategory
      ? [firstCategory, ...remainingCategories]
      : remainingCategories;

    return orderedCategories.map((category) => ({ value: category, label: category }));
  }, [allPurchaseItems, quickButtons]);

  const latestWarehouseImportById = useMemo(() => {
    const map = new Map<string, WarehouseEntry>();
    warehouseEntries
      .filter((entry) => entry.entryType === 'import')
      .forEach((entry) => {
        const key = (entry.coffeeTypeId || '').trim();
        if (!key) return;
        const existing = map.get(key);
        const entryTime = (entry.updatedAt || entry.createdAt || new Date(0)).getTime();
        const existingTime = existing
          ? (existing.updatedAt || existing.createdAt || new Date(0)).getTime()
          : -1;
        if (!existing || entryTime > existingTime) {
          map.set(key, entry);
        }
      });
    return map;
  }, [warehouseEntries]);

  const isGramUnit = (unit?: string) => {
    const key = normalizeText(unit).replace(/\s+/g, '').replace(/\./g, '');
    return ['g', 'gram', 'gam'].includes(key);
  };

  const isKgUnit = (unit?: string) => {
    const key = normalizeText(unit).replace(/\s+/g, '').replace(/\./g, '');
    return ['kg', 'kilo', 'kilogram'].includes(key);
  };

  const getWarehouseDefaultsById = useCallback(
    (warehouseId?: string) => {
      if (!warehouseId) return null;
      const good = warehouseGoods.find((g) => g.id === warehouseId);
      if (!good) return null;

      const baseUnit = (good.baseUnit || 'g').trim();
      const entry = latestWarehouseImportById.get(warehouseId);
      if (!entry) {
        return baseUnit ? { unit: baseUnit } : null;
      }

      let unit = baseUnit || entry.unit || 'g';
      let unitPrice = Number(entry.unitPrice || 0);

      if (entry.amountMode === 'per1000g') {
        if (isGramUnit(baseUnit)) {
          unit = 'g';
          unitPrice = unitPrice / 1000;
        } else if (isKgUnit(baseUnit)) {
          unit = baseUnit;
        }
      } else if (entry.unit) {
        unit = entry.unit;
      }

      return {
        unit,
        unitPrice: roundNumber(unitPrice, 6),
      };
    },
    [latestWarehouseImportById, warehouseGoods],
  );

  /** Chỉ chọn từ danh mục kho tổng; khi sửa phiếu cũ không còn trong danh mục thì thêm một option cảnh báo. */
  const warehouseGoodsSelectOptions = useMemo(() => {
    const base = warehouseGoods.map((c) => ({
      value: c.name,
      label: `${c.name}${c.productCode ? ` (${c.productCode})` : ''}`,
      id: c.id,
    }));
    const name = (editingItem?.itemName || '').trim();
    if (!name) return base;
    const inList = warehouseGoods.some(
      (g) =>
        (editingItem?.warehouseCoffeeId && g.id === editingItem.warehouseCoffeeId) ||
        normalizeText(g.name) === normalizeText(name),
    );
    if (inList) return base;
    return [
      ...base,
      {
        value: name,
        label: `${name} — không còn trong danh mục, chọn lại hàng hợp lệ`,
        id: '',
      },
    ];
  }, [warehouseGoods, editingItem]);

  const recentItemByName = useMemo(() => {
    const sorted = [...allPurchaseItems].sort(
      (a, b) => (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0)
    );
    const map = new Map<string, PurchaseItem>();

    sorted.forEach((item) => {
      const key = normalizeText(item.itemName);
      if (!key || map.has(key)) return;
      map.set(key, item);
    });

    return map;
  }, [allPurchaseItems]);

  const recipeLookup = useMemo(() => createRecipeLookup(recipes), [recipes]);

  const findRecipe = useCallback(
    (invoice: Pick<InvoiceNotification, 'itemId' | 'itemName'>) => recipeLookup.resolve(invoice).recipe,
    [recipeLookup],
  );

  const inventoryFilteredPurchases = useMemo(() => {
    return allPurchaseItems.filter((item) => {
      const itemDate = dayjs(item.date);
      const matchPeriod = isWithinInventoryBalanceCutoff(itemDate, inventoryPeriodFilter, inventorySelectedDate);
      if (!matchPeriod) return false;

      const effectiveStoreId = resolveStoreIdForPurchaseItem(item);
      const matchStore = inventoryStoreFilter === 'all' || effectiveStoreId === inventoryStoreFilter;
      return matchStore;
    });
  }, [allPurchaseItems, inventoryPeriodFilter, inventorySelectedDate, inventoryStoreFilter, resolveStoreIdForPurchaseItem]);

  const inventoryFilteredInvoices = useMemo(() => {
    return invoiceRows.filter((invoice) => {
      const invoiceDate = parseInventoryDate(invoice.checkInDate);
      if (!invoiceDate || !isInventoryInvoiceCountable(invoice)) return false;

      const matchPeriod = isWithinInventoryBalanceCutoff(invoiceDate, inventoryPeriodFilter, inventorySelectedDate);
      if (!matchPeriod) return false;

      const invoiceStoreId = resolveStoreIdFromInvoice(invoice);
      const matchStore = inventoryStoreFilter === 'all' || invoiceStoreId === inventoryStoreFilter;
      return matchStore;
    });
  }, [invoiceRows, inventoryPeriodFilter, inventorySelectedDate, inventoryStoreFilter, resolveStoreIdFromInvoice]);

  const inventoryFilteredPartnerTransactions = useMemo(() => {
    return coffeeTransactions.filter((tx) => {
      if (tx.transactionType !== 'partner_output') return false;

      const txDate = dayjs(tx.date);
      const matchPeriod = isWithinInventoryBalanceCutoff(txDate, inventoryPeriodFilter, inventorySelectedDate);
      if (!matchPeriod) return false;

      const txStoreId = resolveStoreIdFromRawValue(tx.storeName);
      const matchStore = inventoryStoreFilter === 'all' || txStoreId === inventoryStoreFilter;
      return matchStore;
    });
  }, [
    coffeeTransactions,
    inventoryPeriodFilter,
    inventorySelectedDate,
    inventoryStoreFilter,
    resolveStoreIdFromRawValue,
  ]);

  const inventoryBaseRows = useMemo(() => {
    const grouped = new Map<string, IngredientInventoryRow>();

    inventoryFilteredPurchases.forEach((item) => {
      const normalizedName = normalizeText(item.itemName);
      if (!normalizedName) return;

      const unitKey = normalizeText(item.unit || '');
      const effectiveStoreId = resolveStoreIdForPurchaseItem(item) || 'unknown';
      const key = `${normalizedName}__${unitKey || '-'}__${effectiveStoreId}`;
      const existing = grouped.get(key);
      const isTransferOut = (item as any).isTransferOut === true;
      const isTransferIn = (item as any).isTransferIn === true;
      const transferToStoreId = (item as any).transferToStoreId;
      const transferFromStoreId = (item as any).transferFromStoreId;

      const buildTransferLabel = () => {
        if (isTransferOut) {
          const toName = transferToStoreId ? storeNameMap[transferToStoreId] : undefined;
          return `Chuyển đi${toName ? ` → ${toName}` : ''}`;
        }
        if (isTransferIn) {
          const fromName = transferFromStoreId ? storeNameMap[transferFromStoreId] : undefined;
          return `Nhận chuyển${fromName ? ` ← ${fromName}` : ''}`;
        }
        return undefined;
      };

      const transferLabel = buildTransferLabel();

      if (existing) {
        if (!isTransferOut) existing.importCount += 1;
        existing.importedQuantity += Number(item.quantity || 0);
        existing.importedAmount += getPurchaseAmountDelta(item);
        existing.importDetails.push({
          key: `${item.id || `${item.itemName}-${item.createdAt}`}-import`,
          date: item.date ? dayjs(item.date).format('DD/MM/YYYY') : '-',
          storeName: storeNameMap[effectiveStoreId] || '-',
          quantity: Number(item.quantity || 0),
          unit: item.unit || '-',
          unitPrice: Number(item.unitPrice || 0),
          total: getPurchaseAmountDelta(item),
          createdBy: item.createdBy || '-',
          itemName: transferLabel ? `${item.itemName || '-'} (${transferLabel})` : item.itemName || '-',
        });
      } else {
        grouped.set(key, {
          key,
          ingredientName: item.itemName,
          storeLocationId: effectiveStoreId !== 'unknown' ? effectiveStoreId : undefined,
          storeName: storeNameMap[effectiveStoreId] || '-',
          unit: item.unit || '-',
          importCount: isTransferOut ? 0 : 1,
          importedQuantity: Number(item.quantity || 0),
          importedAmount: getPurchaseAmountDelta(item),
          consumedQuantity: 0,
          stockQuantity: 0,
          averageImportPrice: 0,
          estimatedStockAmount: 0,
          sameStoreMatchCount: 0,
          fallbackMissCount: 0,
          importDetails: [
            {
              key: `${item.id || `${item.itemName}-${item.createdAt}`}-import`,
              date: item.date ? dayjs(item.date).format('DD/MM/YYYY') : '-',
              storeName: storeNameMap[effectiveStoreId] || '-',
              quantity: Number(item.quantity || 0),
              unit: item.unit || '-',
              unitPrice: Number(item.unitPrice || 0),
              total: getPurchaseAmountDelta(item),
              createdBy: item.createdBy || '-',
              itemName: transferLabel ? `${item.itemName || '-'} (${transferLabel})` : item.itemName || '-',
            },
          ],
          consumptionDetails: [],
        });
      }
    });

    const importCandidates = Array.from(grouped.entries()).map(([key, row]) => ({
      key,
      ingredientName: row.ingredientName,
      importedQuantity: row.importedQuantity,
      storeLocationId: row.storeLocationId,
      unit: row.unit,
    }));
    const importCandidateByKey = new Map(importCandidates.map((candidate) => [candidate.key, candidate]));

    inventoryFilteredInvoices.forEach((invoice) => {
      const itemQuantity = Number(invoice.quantity || 0);
      if (itemQuantity <= 0) return;

      const invoiceStoreId = resolveStoreIdFromInvoice(invoice);

      const recipe = findRecipe(invoice);
      // Không CT → xuất 1× chính món (đồng bộ tab Nguyên liệu xuất)
      const lineResult = resolveInvoiceLineIngredients(invoice, recipe);
      lineResult.lineItems.forEach((lineItem) => {
        if (lineItem.usedAmount <= 0) return;

        const normalizedName = normalizeText(lineItem.ingredientName);
        if (!normalizedName) return;

        const unitCandidates = importCandidates.filter((candidate) =>
          isStockUnitCompatible(lineItem.unit, candidate.unit)
        );
        const scopedCandidates = invoiceStoreId
          ? unitCandidates.filter((candidate) => candidate.storeLocationId === invoiceStoreId)
          : unitCandidates.filter((candidate) => !candidate.storeLocationId);

        let matchedKey = findBestInventoryMatchKey(lineItem.ingredientName, scopedCandidates);
        let consumedQuantity = lineItem.usedAmount;
        if (matchedKey) {
          const matchedCandidate = importCandidateByKey.get(matchedKey);
          const convertedQuantity = convertIngredientUsageToStockUnit(
            lineItem.usedAmount,
            lineItem.unit,
            matchedCandidate?.unit,
          );
          if (convertedQuantity === null) {
            matchedKey = null;
          } else {
            consumedQuantity = convertedQuantity;
          }
        }
        const matchedBy: IngredientInventoryRow['matchedBy'] = matchedKey ? 'same_store' : 'fallback_new';
        const fallbackKey = `${normalizeIngredientForMatch(lineItem.ingredientName) || normalizedName}__${normalizeText(lineItem.unit)}__${
          invoiceStoreId || 'unknown'
        }`;
        const key = matchedKey || fallbackKey;
        const existing = grouped.get(key);

        if (existing) {
          existing.consumedQuantity += consumedQuantity;
          existing.consumptionDetails.push({
            key: `${invoice.id || invoice.invoiceCode || invoice.orderCode || 'invoice'}-${lineItem.ingredientKey}-${existing.consumptionDetails.length}`,
            invoiceCode: invoice.invoiceCode || '-',
            orderCode: invoice.orderCode || '-',
            checkInDate: invoice.checkInDate || '-',
            checkInTime: invoice.checkInTime || '-',
            store: resolveStoreDisplayNameFromInvoice(invoice),
            itemName: invoice.itemName || '-',
            usedAmount: consumedQuantity,
            formula: lineItem.formula,
            source: lineItem.source,
          });
          if (matchedBy === 'same_store') {
            existing.sameStoreMatchCount = (existing.sameStoreMatchCount || 0) + 1;
            existing.matchedBy = 'same_store';
          } else {
            existing.fallbackMissCount = (existing.fallbackMissCount || 0) + 1;
            if (!existing.matchedBy) {
              existing.matchedBy = 'fallback_new';
            }
          }
        } else {
          grouped.set(key, {
            key,
            ingredientName: lineItem.ingredientName,
            storeLocationId: invoiceStoreId,
            storeName: storeNameMap[invoiceStoreId || ''] || resolveStoreDisplayNameFromInvoice(invoice),
            unit: lineItem.unit,
            importCount: 0,
            importedQuantity: 0,
            importedAmount: 0,
            consumedQuantity,
            stockQuantity: 0,
            averageImportPrice: 0,
            estimatedStockAmount: 0,
            matchedBy,
            sameStoreMatchCount: matchedBy === 'same_store' ? 1 : 0,
            fallbackMissCount: matchedBy === 'fallback_new' ? 1 : 0,
            importDetails: [],
            consumptionDetails: [
              {
                key: `${invoice.id || invoice.invoiceCode || invoice.orderCode || 'invoice'}-${lineItem.ingredientKey}-0`,
                invoiceCode: invoice.invoiceCode || '-',
                orderCode: invoice.orderCode || '-',
                checkInDate: invoice.checkInDate || '-',
                checkInTime: invoice.checkInTime || '-',
                store: resolveStoreDisplayNameFromInvoice(invoice),
                itemName: invoice.itemName || '-',
                usedAmount: consumedQuantity,
                formula: lineItem.formula,
                source: lineItem.source,
              },
            ],
          });
        }
      });
    });

    inventoryFilteredPartnerTransactions.forEach((tx) => {
      const consumedAmount = Number(tx.quantity || 0);
      if (consumedAmount <= 0) return;

      const ingredientName = (tx.coffeeTypeName || '').trim();
      if (!ingredientName) return;

      const normalizedName = normalizeText(ingredientName);
      const txStoreId = resolveStoreIdFromRawValue(tx.storeName);
      const unitCandidates = importCandidates.filter((candidate) =>
        isStockUnitCompatible(tx.unit, candidate.unit)
      );

      const scopedCandidates = txStoreId
        ? unitCandidates.filter((candidate) => candidate.storeLocationId === txStoreId)
        : unitCandidates.filter((candidate) => !candidate.storeLocationId);

      let matchedKey = findBestInventoryMatchKey(ingredientName, scopedCandidates);
      let stockConsumedAmount = consumedAmount;
      if (matchedKey) {
        const matchedCandidate = importCandidateByKey.get(matchedKey);
        const convertedQuantity = convertIngredientUsageToStockUnit(
          consumedAmount,
          tx.unit,
          matchedCandidate?.unit,
        );
        if (convertedQuantity === null) {
          matchedKey = null;
        } else {
          stockConsumedAmount = convertedQuantity;
        }
      }
      const matchedBy: IngredientInventoryRow['matchedBy'] = matchedKey ? 'same_store' : 'fallback_new';
      const fallbackKey = `${normalizeIngredientForMatch(ingredientName) || normalizedName}__${normalizeText(tx.unit || '-')}__${txStoreId || 'unknown'}`;
      const key = matchedKey || fallbackKey;
      const existing = grouped.get(key);

      const txDateLabel = tx.date ? dayjs(tx.date).format('DD/MM/YYYY') : '-';
      const txStoreName = storeNameMap[txStoreId || ''] || (tx.storeName || '-') ;

      if (existing) {
        existing.consumedQuantity += stockConsumedAmount;
        existing.consumptionDetails.push({
          key: `${tx.id || `${ingredientName}-${txDateLabel}`}-partner-${existing.consumptionDetails.length}`,
          invoiceCode: '-',
          orderCode: tx.id ? `TX-${tx.id.slice(-6)}` : 'TX',
          checkInDate: txDateLabel,
          checkInTime: '-',
          store: txStoreName,
          itemName: tx.partnerName ? `${ingredientName} -> ${tx.partnerName}` : ingredientName,
          usedAmount: stockConsumedAmount,
          formula: `${consumedAmount} ${tx.unit || '-'}`,
          source: `Xuất đối tác${tx.partnerName ? `: ${tx.partnerName}` : ''}`,
        });

        if (matchedBy === 'same_store') {
          existing.sameStoreMatchCount = (existing.sameStoreMatchCount || 0) + 1;
          existing.matchedBy = 'same_store';
        } else {
          existing.fallbackMissCount = (existing.fallbackMissCount || 0) + 1;
          if (!existing.matchedBy) {
            existing.matchedBy = 'fallback_new';
          }
        }
      } else {
        grouped.set(key, {
          key,
          ingredientName,
          storeLocationId: txStoreId,
          storeName: txStoreName,
          unit: (tx.unit || '').trim() || '-',
          importCount: 0,
          importedQuantity: 0,
          importedAmount: 0,
          consumedQuantity: stockConsumedAmount,
          stockQuantity: 0,
          averageImportPrice: 0,
          estimatedStockAmount: 0,
          matchedBy,
          sameStoreMatchCount: matchedBy === 'same_store' ? 1 : 0,
          fallbackMissCount: matchedBy === 'fallback_new' ? 1 : 0,
          importDetails: [],
          consumptionDetails: [
            {
              key: `${tx.id || `${ingredientName}-${txDateLabel}`}-partner-0`,
              invoiceCode: '-',
              orderCode: tx.id ? `TX-${tx.id.slice(-6)}` : 'TX',
              checkInDate: txDateLabel,
              checkInTime: '-',
              store: txStoreName,
              itemName: tx.partnerName ? `${ingredientName} -> ${tx.partnerName}` : ingredientName,
              usedAmount: stockConsumedAmount,
              formula: `${consumedAmount} ${tx.unit || '-'}`,
              source: `Xuất đối tác${tx.partnerName ? `: ${tx.partnerName}` : ''}`,
            },
          ],
        });
      }
    });

    return Array.from(grouped.values()).map((row) => {
      const averageImportPrice = row.importedQuantity > 0 ? row.importedAmount / row.importedQuantity : 0;
      const stockQuantity = row.importedQuantity - row.consumedQuantity;
      const estimatedStockAmount = stockQuantity * averageImportPrice;

      return {
        ...row,
        stockQuantity,
        averageImportPrice,
        estimatedStockAmount,
      };
    });

  }, [
    inventoryFilteredPurchases,
    inventoryFilteredInvoices,
    inventoryFilteredPartnerTransactions,
    findRecipe,
    resolveStoreIdForPurchaseItem,
    resolveStoreIdFromInvoice,
    resolveStoreIdFromRawValue,
    resolveStoreDisplayNameFromInvoice,
    storeNameMap,
  ]);


  const inventoryRows = useMemo(() => {
    const keyword = normalizeText(inventorySearchTerm);
    const filteredRows = keyword
      ? inventoryBaseRows.filter((row) => normalizeText(row.ingredientName).includes(keyword))
      : inventoryBaseRows;

    return [...filteredRows].sort((a, b) => b.estimatedStockAmount - a.estimatedStockAmount);
  }, [inventoryBaseRows, inventorySearchTerm]);

  const inventoryMatchStats = useMemo(() => {
    return inventoryBaseRows.reduce(
      (acc, row) => {
        if ((row.fallbackMissCount || 0) > 0) {
          acc.unmatchedRows += 1;
          acc.unmatchedMatches += row.fallbackMissCount || 0;
        }

        return acc;
      },
      {
        unmatchedRows: 0,
        unmatchedMatches: 0,
      }
    );
  }, [inventoryBaseRows]);

  const inventoryDisplayRows = useMemo(() => {
    if (inventoryStoreFilter !== 'all') return inventoryRows;

    const grouped = new Map<string, IngredientInventoryRow>();

    inventoryRows.forEach((row) => {
      const groupKey = `${normalizeText(row.ingredientName)}__${normalizeText(row.unit || '-')}`;
      const existing = grouped.get(groupKey);

      if (existing) {
        existing.importCount += row.importCount;
        existing.importedQuantity += row.importedQuantity;
        existing.importedAmount += row.importedAmount;
        existing.consumedQuantity += row.consumedQuantity;
        existing.importDetails = [...existing.importDetails, ...row.importDetails];
        existing.consumptionDetails = [...existing.consumptionDetails, ...row.consumptionDetails];
        existing.sameStoreMatchCount = (existing.sameStoreMatchCount || 0) + (row.sameStoreMatchCount || 0);
        existing.fallbackMissCount = (existing.fallbackMissCount || 0) + (row.fallbackMissCount || 0);

        if (row.matchedBy === 'same_store') {
          existing.matchedBy = 'same_store';
        } else if (!existing.matchedBy && row.matchedBy === 'fallback_new') {
          existing.matchedBy = 'fallback_new';
        }
      } else {
        grouped.set(groupKey, {
          ...row,
          key: groupKey,
          storeLocationId: undefined,
          storeName: 'Tất cả cơ sở',
        });
      }
    });

    return Array.from(grouped.values())
      .map((row) => {
        const averageImportPrice = row.importedQuantity > 0 ? row.importedAmount / row.importedQuantity : 0;
        const stockQuantity = row.importedQuantity - row.consumedQuantity;
        const estimatedStockAmount = stockQuantity * averageImportPrice;

        return {
          ...row,
          stockQuantity,
          averageImportPrice,
          estimatedStockAmount,
        };
      })
      .sort((a, b) => b.estimatedStockAmount - a.estimatedStockAmount);
  }, [inventoryRows, inventoryStoreFilter]);

  const inventoryStats = useMemo(() => {
    return inventoryDisplayRows.reduce(
      (acc, row) => {
        acc.importCount += row.importCount;
        acc.totalImportedAmount += row.importedAmount;
        acc.totalEstimatedStockAmount += row.estimatedStockAmount;
        return acc;
      },
      {
        importCount: 0,
        totalImportedAmount: 0,
        totalEstimatedStockAmount: 0,
      }
    );
  }, [inventoryDisplayRows]);

  const outputStoreOptions = useMemo(() => {
    const optionMap = new Map<string, string>();

    invoiceRows.forEach((invoice) => {
      const storeId = resolveStoreIdFromInvoice(invoice);
      const storeDisplayName = resolveStoreDisplayNameFromInvoice(invoice);
      const rawStore = (invoice.store || '').trim();

      if (storeId) {
        optionMap.set(storeId, storeNameMap[storeId] || storeDisplayName || rawStore || storeId);
        return;
      }

      if (rawStore) {
        optionMap.set(`raw:${rawStore}`, rawStore);
      }
    });

    const sortedOptions = Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));

    return [
      { label: 'Tất cả cơ sở', value: 'all' },
      ...sortedOptions,
    ];
  }, [invoiceRows, resolveStoreIdFromInvoice, resolveStoreDisplayNameFromInvoice, storeNameMap]);

  const outputFilteredInvoices = useMemo(() => {
    const keyword = normalizeText(outputSearchTerm);

    return invoiceRows.filter((invoice) => {
      const invoiceDate = parseInventoryDate(invoice.checkInDate);
      if (!invoiceDate || !isInventoryInvoiceCountable(invoice)) return false;

      const matchPeriod = isInPeriod(invoiceDate, outputPeriodFilter, outputSelectedDate);
      if (!matchPeriod) return false;

      const invoiceStoreId = resolveStoreIdFromInvoice(invoice);
      const rawStore = (invoice.store || '').trim();
      const isRawStoreFilter = outputStoreFilter.startsWith('raw:');

      const matchStore =
        outputStoreFilter === 'all' ||
        (!isRawStoreFilter && outputStoreFilter === invoiceStoreId) ||
        (isRawStoreFilter && rawStore === outputStoreFilter.slice(4));
      if (!matchStore) return false;

      if (!keyword) return true;

      const textBag = normalizeText(
        `${invoice.itemName || ''} ${invoice.itemId || ''} ${invoice.invoiceCode || ''} ${invoice.orderCode || ''}`
      );
      return textBag.includes(keyword);
    });
  }, [invoiceRows, outputPeriodFilter, outputSelectedDate, outputStoreFilter, outputSearchTerm, resolveStoreIdFromInvoice]);

  const outputInvoiceGroups = useMemo(() => {
    const groupMap = new Map<string, InvoiceGroupRow>();

    outputFilteredInvoices.forEach((row) => {
      const invoiceIdentity = (row.orderCode || row.invoiceCode || '').trim() || 'NO_CODE';
      const datePart = (row.checkInDate || '').trim();
      const timePart = (row.checkInTime || '').trim();
      const groupKey = `${invoiceIdentity}__${datePart}__${timePart}`;

      const itemQty = Number(row.quantity || 0);
      const existing = groupMap.get(groupKey);
      if (existing) {
        existing.rows.push(row);
        if (row.itemName && !existing.itemNames.includes(row.itemName)) {
          existing.itemNames.push(row.itemName);
        }

        const bonusOptions = formatBonusOptions(row.selectedOptions || []);
        bonusOptions.forEach((bonusLabel) => {
          if (!existing.bonusOptionNames.includes(bonusLabel)) {
            existing.bonusOptionNames.push(bonusLabel);
          }
        });

        existing.lineCount += 1;
        existing.totalItemQuantity += itemQty > 0 ? itemQty : 0;
      } else {
        const bonusOptions = formatBonusOptions(row.selectedOptions || []);

        groupMap.set(groupKey, {
          key: groupKey,
          invoiceCode: row.invoiceCode || '-',
          orderCode: row.orderCode || '-',
          checkInDate: row.checkInDate || '-',
          checkInTime: row.checkInTime || '-',
          store: resolveStoreDisplayNameFromInvoice(row),
          itemNames: row.itemName ? [row.itemName] : [],
          bonusOptionNames: bonusOptions,
          lineCount: 1,
          totalItemQuantity: itemQty > 0 ? itemQty : 0,
          totalAmount: Number(row.totalAmount || 0),
          rows: [row],
        });
      }
    });

    const grouped = Array.from(groupMap.values());
    const itemKeyword = normalizeText(invoiceItemSearchTerm);

    const filteredByItemName = itemKeyword
      ? grouped.filter((group) =>
          normalizeText(group.itemNames.join(' | ')).includes(itemKeyword)
        )
      : grouped;

    return filteredByItemName.sort((a, b) => {
      const aDate = parseInventoryDate(a.checkInDate);
      const bDate = parseInventoryDate(b.checkInDate);
      const aTs = aDate ? aDate.valueOf() : 0;
      const bTs = bDate ? bDate.valueOf() : 0;

      if (aTs !== bTs) return bTs - aTs;
      return (b.checkInTime || '').localeCompare(a.checkInTime || '');
    });
  }, [outputFilteredInvoices, invoiceItemSearchTerm, resolveStoreDisplayNameFromInvoice]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(outputInvoiceGroups.length / invoiceTablePageSize));
    if (invoiceTablePage > maxPage) {
      setInvoiceTablePage(maxPage);
    }
  }, [outputInvoiceGroups.length, invoiceTablePage, invoiceTablePageSize]);

  const ingredientOutputData = useMemo(() => {
    const ingredientMap = new Map<string, IngredientOutputRow>();
    const missMap = new Map<string, RecipeMissRow>();

    for (const invoice of outputFilteredInvoices) {
      const itemQuantity = Number(invoice.quantity || 0);
      if (itemQuantity <= 0) continue;

      const recipe = findRecipe(invoice);
      const lineResult = resolveInvoiceLineIngredients(invoice, recipe);

      // Ghi nhận riêng món không có CT để admin bổ sung; không tự suy đoán nguyên liệu xuất.
      if (!recipe) {
        const missKey = `${invoice.itemId || 'NO_ITEM_ID'}__${normalizeText(invoice.itemName)}`;
        const existingMiss = missMap.get(missKey);
        if (existingMiss) {
          existingMiss.count += 1;
          existingMiss.quantity += itemQuantity;
        } else {
          missMap.set(missKey, {
            key: missKey,
            itemId: invoice.itemId || '-',
            itemName: invoice.itemName || '-',
            count: 1,
            quantity: itemQuantity,
          });
        }
      }

      lineResult.lineItems.forEach((lineItem) => {
        const usageKey = `${lineItem.ingredientKey}__${normalizeText(lineItem.unit)}`;
        const existing = ingredientMap.get(usageKey);
        if (existing) {
          existing.totalAmount += lineItem.usedAmount;
          existing.invoiceCount += 1;
        } else {
          ingredientMap.set(usageKey, {
            key: usageKey,
            ingredientName: lineItem.ingredientName,
            unit: lineItem.unit,
            totalAmount: lineItem.usedAmount,
            invoiceCount: 1,
          });
        }
      });
    }

    const ingredientRows = Array.from(ingredientMap.values()).sort((a, b) => b.totalAmount - a.totalAmount);
    const missingRows = Array.from(missMap.values()).sort((a, b) => b.count - a.count);

    return {
      ingredientRows,
      missingRows,
      sourceRowCount: outputFilteredInvoices.length,
    };
  }, [
    outputFilteredInvoices,
    findRecipe,
  ]);

  const filteredIngredientRows = useMemo(() => {
    const keyword = normalizeText(ingredientSearchTerm);
    if (!keyword) return ingredientOutputData.ingredientRows;

    return ingredientOutputData.ingredientRows.filter((row) =>
      normalizeText(row.ingredientName).includes(keyword)
    );
  }, [ingredientOutputData.ingredientRows, ingredientSearchTerm]);

  const inventoryMissingRecipeCount = useMemo(() => {
    const missingKeys = new Set<string>();
    inventoryFilteredInvoices.forEach((invoice) => {
      if (findRecipe(invoice)) return;
      missingKeys.add(`${invoice.itemId || 'NO_ITEM_ID'}__${normalizeText(invoice.itemName)}`);
    });
    return missingKeys.size;
  }, [inventoryFilteredInvoices, findRecipe]);

  const selectedInvoiceFormulaData = useMemo(() => {
    if (!selectedInvoiceGroup) {
      return {
        lineRows: [] as InvoiceLineFormulaRow[],
        ingredientRows: [] as InvoiceIngredientFormulaRow[],
        missingCount: 0,
        optionWarnings: [] as string[],
      };
    }

    const lineRows: InvoiceLineFormulaRow[] = [];
    const ingredientRows: InvoiceIngredientFormulaRow[] = [];
    let missingCount = 0;
    const optionWarnings: string[] = [];

    selectedInvoiceGroup.rows.forEach((invoice, rowIndex) => {
      const itemQty = Number(invoice.quantity || 0);
      const recipe = findRecipe(invoice);
      const selectedOptions = invoice.selectedOptions || [];
      const lineResult = resolveInvoiceLineIngredients(invoice, recipe);
      optionWarnings.push(...findMissingOptionAddonWarnings(invoice, recipe).map(
        (warning) => `${invoice.itemName || invoice.itemId || 'Món không tên'}: ${warning}`
      ));
      const totalLineUsed = lineResult.lineItems.reduce((sum, item) => sum + item.usedAmount, 0);

      if (!recipe) {
        missingCount += 1;
      }

      lineResult.lineItems.forEach((lineItem, ingIndex) => {
        ingredientRows.push({
          key: `line-${rowIndex}-${ingIndex}`,
          itemName: invoice.itemName || '-',
          ingredientName: lineItem.ingredientName,
          source: lineItem.source,
          formula: lineItem.formula,
          usedAmount: lineItem.usedAmount,
          unit: lineItem.unit,
        });
      });

      lineRows.push({
        key: `line-${rowIndex}`,
        itemName: invoice.itemName || '-',
        itemId: invoice.itemId || '-',
        quantity: itemQty,
        mode: lineResult.modeLabel,
        bonusOptions: formatBonusOptions(selectedOptions).join(', ') || '-',
        recipeStatus: lineResult.recipeStatus,
        totalUsedAmount: totalLineUsed,
      });
    });

    return {
      lineRows,
      ingredientRows,
      missingCount,
      optionWarnings,
    };
  }, [selectedInvoiceGroup, findRecipe]);

  const invoiceGroupColumns = [
    {
      title: 'Mã hóa đơn',
      dataIndex: 'invoiceCode',
      key: 'invoiceCode',
      width: 150,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.invoiceCode || '').localeCompare(b.invoiceCode || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Order code',
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 180,
      render: (value: string) => value || '-',
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.orderCode || '').localeCompare(b.orderCode || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Ngày',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      width: 120,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) => {
        const aTs = parseInventoryDate(a.checkInDate)?.valueOf() || 0;
        const bTs = parseInventoryDate(b.checkInDate)?.valueOf() || 0;
        return aTs - bTs;
      },
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Giờ',
      dataIndex: 'checkInTime',
      key: 'checkInTime',
      width: 100,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.checkInTime || '').localeCompare(b.checkInTime || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Cơ sở',
      dataIndex: 'store',
      key: 'store',
      width: 170,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.store || '').localeCompare(b.store || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tên món trong HĐ',
      key: 'itemNames',
      width: 320,
      render: (_: unknown, record: InvoiceGroupRow) =>
        record.itemNames.length ? record.itemNames.join(', ') : '-',
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.itemNames.join(', ') || '').localeCompare(b.itemNames.join(', ') || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Bonus option đã dùng',
      key: 'bonusOptionNames',
      width: 320,
      render: (_: unknown, record: InvoiceGroupRow) =>
        record.bonusOptionNames.length ? record.bonusOptionNames.join(', ') : '-',
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.bonusOptionNames.join(', ') || '').localeCompare(b.bonusOptionNames.join(', ') || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Số dòng món',
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) => (a.lineCount || 0) - (b.lineCount || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng SL món',
      dataIndex: 'totalItemQuantity',
      key: 'totalItemQuantity',
      width: 130,
      align: 'right' as const,
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) =>
        (a.totalItemQuantity || 0) - (b.totalItemQuantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng tiền HĐ',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
      sorter: (a: InvoiceGroupRow, b: InvoiceGroupRow) => (a.totalAmount || 0) - (b.totalAmount || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
  ];

  const formulaLineColumns = [
    {
      title: 'Món',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 220,
    },
    {
      title: 'Mã món',
      dataIndex: 'itemId',
      key: 'itemId',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: 'SL món',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      align: 'right' as const,
    },
    {
      title: 'Mode áp dụng',
      dataIndex: 'mode',
      key: 'mode',
      width: 120,
    },
    {
      title: 'Bonus option đã dùng',
      dataIndex: 'bonusOptions',
      key: 'bonusOptions',
      width: 250,
    },
    {
      title: 'Trạng thái CT',
      dataIndex: 'recipeStatus',
      key: 'recipeStatus',
      width: 130,
      render: (value: string) => {
        if (value === 'Đã tính') return <Tag color="green">Đã tính</Tag>;
        if (value === 'Tự động (1× món)') return <Tag color="blue">Tự động 1× món</Tag>;
        return <Tag color="volcano">{value || 'Thiếu CT'}</Tag>;
      },
    },
    {
      title: 'Tổng lượng NL',
      dataIndex: 'totalUsedAmount',
      key: 'totalUsedAmount',
      width: 130,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
  ];

  const formulaIngredientColumns = [
    {
      title: 'Món',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
    },
    {
      title: 'Nguyên liệu',
      dataIndex: 'ingredientName',
      key: 'ingredientName',
      width: 180,
    },
    {
      title: 'Nguồn tính',
      dataIndex: 'source',
      key: 'source',
      width: 220,
    },
    {
      title: 'Công thức',
      dataIndex: 'formula',
      key: 'formula',
      width: 140,
    },
    {
      title: 'Lượng lấy',
      dataIndex: 'usedAmount',
      key: 'usedAmount',
      width: 120,
      align: 'right' as const,
      render: (value: number, record: InvoiceIngredientFormulaRow) =>
        `${Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${record.unit}`,
    },
  ];

  const ingredientOutputColumns = [
    {
      title: 'Nguyên liệu',
      dataIndex: 'ingredientName',
      key: 'ingredientName',
      width: 260,
      sorter: (a: IngredientOutputRow, b: IngredientOutputRow) =>
        (a.ingredientName || '').localeCompare(b.ingredientName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng lượng đã lấy',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 180,
      align: 'right' as const,
      sorter: (a: IngredientOutputRow, b: IngredientOutputRow) =>
        (a.totalAmount || 0) - (b.totalAmount || 0),
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number, record: IngredientOutputRow) =>
        `${value.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} ${record.unit}`,
    },
    {
      title: 'Số dòng hóa đơn áp dụng',
      dataIndex: 'invoiceCount',
      key: 'invoiceCount',
      width: 180,
      align: 'right' as const,
      sorter: (a: IngredientOutputRow, b: IngredientOutputRow) =>
        (a.invoiceCount || 0) - (b.invoiceCount || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
  ];

  const missingRecipeColumns = [
    {
      title: 'Mã món',
      dataIndex: 'itemId',
      key: 'itemId',
      width: 170,
      sorter: (a: RecipeMissRow, b: RecipeMissRow) =>
        (a.itemId || '').localeCompare(b.itemId || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: string) => value || '-',
    },
    {
      title: 'Tên món',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 220,
      sorter: (a: RecipeMissRow, b: RecipeMissRow) =>
        (a.itemName || '').localeCompare(b.itemName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Số dòng thiếu CT',
      dataIndex: 'count',
      key: 'count',
      width: 140,
      align: 'right' as const,
      sorter: (a: RecipeMissRow, b: RecipeMissRow) => (a.count || 0) - (b.count || 0),
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng SL món',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 130,
      align: 'right' as const,
      sorter: (a: RecipeMissRow, b: RecipeMissRow) => (a.quantity || 0) - (b.quantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Chi tiết',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: RecipeMissRow) => (
        <Button
          type="link"
          onClick={(event) => {
            event.stopPropagation();
            openMissingRecipeModal(record);
          }}
        >
          Tạo công thức
        </Button>
      ),
    },
  ];

  const inventoryColumns = [
    {
      title: 'Nguyên liệu',
      dataIndex: 'ingredientName',
      key: 'ingredientName',
      width: 220,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.ingredientName || '').localeCompare(b.ingredientName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Cơ sở',
      dataIndex: 'storeName',
      key: 'storeName',
      width: 170,
      render: (value: string) => value || '-',
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.storeName || '').localeCompare(b.storeName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Đơn vị',
      dataIndex: 'unit',
      key: 'unit',
      width: 90,
      render: (value: string) => value || '-',
    },
    {
      title: 'SL nhập',
      dataIndex: 'importedQuantity',
      key: 'importedQuantity',
      width: 120,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.importedQuantity || 0) - (b.importedQuantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
    {
      title: 'SL lấy',
      dataIndex: 'consumedQuantity',
      key: 'consumedQuantity',
      width: 120,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.consumedQuantity || 0) - (b.consumedQuantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Tồn ước tính',
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      width: 130,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.stockQuantity || 0) - (b.stockQuantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => {
        const color = value < 0 ? 'volcano' : value === 0 ? 'default' : 'green';
        return <Tag color={color}>{Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}</Tag>;
      },
    },
    {
      title: 'Tiền nhập',
      dataIndex: 'importedAmount',
      key: 'importedAmount',
      width: 130,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.importedAmount || 0) - (b.importedAmount || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
    },
    {
      title: 'Đơn giá TB nhập',
      dataIndex: 'averageImportPrice',
      key: 'averageImportPrice',
      width: 130,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.averageImportPrice || 0) - (b.averageImportPrice || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
    },
    {
      title: 'Giá trị tồn ước tính',
      dataIndex: 'estimatedStockAmount',
      key: 'estimatedStockAmount',
      width: 170,
      align: 'right' as const,
      sorter: (a: IngredientInventoryRow, b: IngredientInventoryRow) =>
        (a.estimatedStockAmount || 0) - (b.estimatedStockAmount || 0),
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => <b>{Number(value || 0).toLocaleString('vi-VN')}</b>,
    },
    {
      title: 'Chi tiết',
      key: 'detail',
      fixed: 'right' as const,
      width: 110,
      render: (_: unknown, record: IngredientInventoryRow) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedInventoryRow(record);
            setInventoryDetailModalOpen(true);
          }}
        >
          Xem
        </Button>
      ),
    },
  ];

  const inventoryImportDetailColumns = [
    {
      title: 'Ngày',
      dataIndex: 'date',
      key: 'date',
      width: 110,
    },
    {
      title: 'Tên nhập',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 220,
    },
    {
      title: 'Cơ sở',
      dataIndex: 'storeName',
      key: 'storeName',
      width: 160,
    },
    {
      title: 'SL nhập',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Đơn vị',
      dataIndex: 'unit',
      key: 'unit',
      width: 90,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
    },
    {
      title: 'Thành tiền',
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN'),
    },
    {
      title: 'Người nhập',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 140,
    },
  ];

  const inventoryConsumptionDetailColumns = [
    {
      title: 'HĐ',
      dataIndex: 'invoiceCode',
      key: 'invoiceCode',
      width: 130,
    },
    {
      title: 'Order code',
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 140,
    },
    {
      title: 'Ngày giờ',
      key: 'checkInDateTime',
      width: 170,
      render: (_: unknown, record: IngredientInventoryConsumeDetail) => `${record.checkInDate} ${record.checkInTime}`,
    },
    {
      title: 'Cơ sở',
      dataIndex: 'store',
      key: 'store',
      width: 160,
    },
    {
      title: 'Món',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
    },
    {
      title: 'SL lấy',
      dataIndex: 'usedAmount',
      key: 'usedAmount',
      width: 110,
      align: 'right' as const,
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      key: 'source',
      width: 250,
    },
    {
      title: 'Công thức',
      dataIndex: 'formula',
      key: 'formula',
      width: 180,
    },
  ];

  const openCreateModal = () => {
    setEditingItem(null);
    setFromWarehouse(false);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(),
      quantity: 1,
      unitPrice: 0,
      total: 0,
    });
    setModalOpen(true);
    if (!allPurchaseItemsLoaded) loadAllPurchaseItems();
  };

  const openEditModal = (record: PurchaseItem) => {
    if (!canEditPurchase) {
      message.warning('Chỉ admin mới được chỉnh sửa đơn nhập liệu.');
      return;
    }

    setEditingItem(record);
    setFromWarehouse(record.fromWarehouse === true);
    form.setFieldsValue({
      code: record.code,
      category: record.category,
      supplierSource: record.supplierSource,
      supplierId: record.supplierId,
      date: dayjs(record.date),
      itemName: record.itemName,
      description: record.description,
      quantity: record.quantity,
      unit: record.unit,
      unitPrice: record.unitPrice,
      total: record.total,
      storeLocationId: record.storeLocationId,
      warehouseCoffeeId: record.warehouseCoffeeId,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFromWarehouse(false);
    form.resetFields();
  };

  const applyRecentDefaultsByItemName = (itemName: string) => {
    const key = normalizeText(itemName);
    if (!key) return;

    const lastItem = recentItemByName.get(key);
    if (!lastItem) return;

    const currentValues = form.getFieldsValue();
    const mergedUnitPrice = Number(currentValues.unitPrice || lastItem.unitPrice || 0);
    const mergedQuantity = Number(currentValues.quantity || 0);
    const catalogMatch = warehouseGoods.find((g) => normalizeText(g.name) === key);

    form.setFieldsValue({
      code: currentValues.code || lastItem.code,
      category: currentValues.category || lastItem.category,
        supplierSource: currentValues.supplierSource || lastItem.supplierSource,
        supplierId: currentValues.supplierId || lastItem.supplierId,
      itemName: catalogMatch?.name || itemName,
      warehouseCoffeeId: catalogMatch?.id,
      unit: currentValues.unit || lastItem.unit,
      unitPrice: mergedUnitPrice,
      total: mergedQuantity * mergedUnitPrice,
    });
  };

  const applyQuickButton = (template: QuickButtonTemplate) => {
    const currentValues = form.getFieldsValue();
    const quantity = Number(currentValues.quantity || 0);
    const unitPrice = Number(template.unitPrice || currentValues.unitPrice || 0);
    const tplName = (template.itemName || '').trim();
    const catalogMatch = tplName
      ? warehouseGoods.find((g) => normalizeText(g.name) === normalizeText(tplName))
      : undefined;

    form.setFieldsValue({
      code: template.code || currentValues.code,
      category: template.category || currentValues.category,
      itemName: catalogMatch?.name,
      warehouseCoffeeId: catalogMatch?.id,
      description: template.description || currentValues.description,
      unit: template.unit || currentValues.unit,
      unitPrice,
      total: quantity * unitPrice,
      storeLocationId: template.storeLocationId || currentValues.storeLocationId,
    });

    if (tplName && !catalogMatch) {
      message.warning('Tên trên nút không trùng danh mục hàng hóa kho tổng — chọn hàng trong danh sách.');
    }
  };

  const handleFormValuesChange = (_: any, values: PurchaseFormValues) => {
    const quantity = Number(values.quantity || 0);
    const unitPrice = Number(values.unitPrice || 0);
    const total = quantity * unitPrice;
    if (values.total !== total) {
      form.setFieldValue('total', total);
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (!canAccessDataEntry) {
      message.error('Bạn không có quyền thao tác nhập liệu.');
      return;
    }

    try {
      const values = await form.validateFields();
      const catalogGood = warehouseGoods.find((g) => g.id === values.warehouseCoffeeId);
      if (!catalogGood?.id) {
        message.error('Chỉ được chọn nguyên liệu có trong danh mục hàng hóa kho tổng.');
        return;
      }

      const normalizedMeasurement = normalizePurchaseMeasurement(
        Number(values.quantity),
        values.unit,
        Number(values.unitPrice),
      );

      const payload = {
        code: values.code?.trim(),
        category: values.category?.trim(),
        supplierSource: values.supplierSource?.trim(),
        supplierId: values.supplierId,
        date: values.date.toDate(),
        itemName: catalogGood.name.trim(),
        description: (values.description || '').trim(),
        quantity: normalizedMeasurement.quantity,
        unit: normalizedMeasurement.unit,
        unitPrice: normalizedMeasurement.unitPrice,
        total: normalizedMeasurement.total,
        createdBy: currentUser?.userId || getCurrentUserId(),
        storeLocationId: values.storeLocationId,
        fromWarehouse,
        warehouseCoffeeId: catalogGood.id,
        warehouseCoffeeName: catalogGood.name,
      };

      setSaving(true);
      if (editingItem?.id) {
        if (!canEditPurchase) {
          message.warning('Chỉ admin mới được chỉnh sửa đơn nhập liệu.');
          return;
        }
        await purchaseService.updatePurchaseItem(editingItem.id, payload);

        // Cập nhật export entry trong warehouse-entries
        if (fromWarehouse && values.storeLocationId) {
          // Xóa export cũ rồi tạo lại
          await warehouseService.deleteExportByPurchaseItemId(editingItem.id);
          const storeName = stores.find((s) => getStoreOptionValue(s) === values.storeLocationId)?.name || values.storeLocationId;
          const wx = buildWarehouseExportMeasurement(normalizedMeasurement, catalogGood);
          await warehouseService.addExportEntry({
            month: values.date.format('YYYY-MM'),
            coffeeTypeId: catalogGood.id,
            coffeeTypeName: catalogGood.name,
            quantity: wx.quantity,
            unitPrice: wx.unitPrice,
            unit: wx.unit,
            amountMode: wx.amountMode,
            storeLocationId: values.storeLocationId,
            storeName,
            purchaseItemId: editingItem.id,
            notes: `Cơ sở ${storeName} lấy từ kho tổng`,
            createdBy: currentUser?.userId || getCurrentUserId(),
          });
        } else if (!fromWarehouse && editingItem.fromWarehouse) {
          // Đã bỏ "lấy từ kho tổng" → xóa export entry cũ
          await warehouseService.deleteExportByPurchaseItemId(editingItem.id);
        }

        message.success(
          normalizedMeasurement.converted
            ? `Đã cập nhật phiếu nhập nguyên liệu và chuẩn hóa đơn vị ${normalizedMeasurement.inputUnit} → ${normalizedMeasurement.unit}.`
            : 'Đã cập nhật phiếu nhập nguyên liệu.'
        );
      } else {
        const newItem = await purchaseService.addPurchaseItem(payload);

        // Tạo export entry trong warehouse-entries
        if (fromWarehouse && values.storeLocationId && newItem?.id) {
          const storeName = stores.find((s) => getStoreOptionValue(s) === values.storeLocationId)?.name || values.storeLocationId;
          const wx = buildWarehouseExportMeasurement(normalizedMeasurement, catalogGood);
          await warehouseService.addExportEntry({
            month: values.date.format('YYYY-MM'),
            coffeeTypeId: catalogGood.id,
            coffeeTypeName: catalogGood.name,
            quantity: wx.quantity,
            unitPrice: wx.unitPrice,
            unit: wx.unit,
            amountMode: wx.amountMode,
            storeLocationId: values.storeLocationId,
            storeName,
            purchaseItemId: newItem.id,
            notes: `Cơ sở ${storeName} lấy từ kho tổng`,
            createdBy: currentUser?.userId || getCurrentUserId(),
          });
        }

        message.success(
          normalizedMeasurement.converted
            ? `Đã tạo phiếu nhập nguyên liệu mới và chuẩn hóa đơn vị ${normalizedMeasurement.inputUnit} → ${normalizedMeasurement.unit}.`
            : 'Đã tạo phiếu nhập nguyên liệu mới.'
        );
      }

      purchaseListCacheRef.current.clear();
      closeModal();
      await Promise.all([
        loadPurchaseItems(periodFilter, selectedDate, true),
        allPurchaseItemsLoaded ? loadAllPurchaseItems(true) : Promise.resolve(),
      ]);
    } catch (error) {
      if ((error as any)?.errorFields) {
        return;
      }
      message.error(error instanceof Error ? error.message : 'Không thể lưu phiếu nhập nguyên liệu.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record?: PurchaseItem | null) => {
    const id = record?.id;
    if (!id) return;
    if (!canEditPurchase) {
      message.warning('Chỉ admin mới được xóa đơn nhập liệu.');
      return;
    }

    try {
      const isTransfer = (record as any)?.isTransferOut === true || (record as any)?.isTransferIn === true;
      if (isTransfer) {
        await purchaseService.deleteStoreTransfer(id);
        message.success('Đã hủy chuyển hàng.');
      } else {
        // Xóa export entry liên kết (nếu có) trước khi xóa phiếu nhập
        await warehouseService.deleteExportByPurchaseItemId(id);
        await purchaseService.deletePurchaseItem(id);
        message.success('Đã xóa phiếu nhập nguyên liệu.');
      }
      purchaseListCacheRef.current.clear();
      await Promise.all([
        loadPurchaseItems(periodFilter, selectedDate, true),
        allPurchaseItemsLoaded ? loadAllPurchaseItems(true) : Promise.resolve(),
      ]);
    } catch (error) {
      const isTransfer = (record as any)?.isTransferOut === true || (record as any)?.isTransferIn === true;
      message.error(isTransfer ? 'Không thể hủy chuyển hàng.' : 'Không thể xóa phiếu nhập nguyên liệu.');
    }
  };

  const openTransferModal = (record: PurchaseItem) => {
    const effectiveStoreId = resolveStoreIdForPurchaseItem(record);
    if (!effectiveStoreId) {
      message.error('Không xác định được cơ sở nguồn của phiếu.');
      return;
    }
    setTransferSourceItem(record);
    transferForm.resetFields();
    transferForm.setFieldsValue({
      date: dayjs(record.date || new Date()),
      toStoreId: '',
      quantity: 0,
      notes: '',
    });
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setTransferSourceItem(null);
    transferForm.resetFields();
  };

  const resolveInventoryStockForItem = useCallback(
    (record: PurchaseItem): number => {
      const effectiveStoreId = resolveStoreIdForPurchaseItem(record);
      const normalizedName = normalizeText(record.itemName);
      const unitKey = normalizeText(record.unit || '');
      if (!effectiveStoreId || !normalizedName) return 0;
      const rowKey = `${normalizedName}__${unitKey || '-'}__${effectiveStoreId}`;
      const row = inventoryBaseRows.find((r) => r.key === rowKey);
      return Number(row?.stockQuantity || 0);
    },
    [inventoryBaseRows, resolveStoreIdForPurchaseItem],
  );

  const handleSubmitTransfer = async () => {
    if (!canAccessDataEntry) {
      message.error('Bạn không có quyền thao tác nhập liệu.');
      return;
    }
    if (!transferSourceItem) return;

    try {
      const values = await transferForm.validateFields();
      const fromStoreId = resolveStoreIdForPurchaseItem(transferSourceItem);
      const toStoreId = String(values.toStoreId || '').trim();
      if (!fromStoreId) {
        message.error('Không xác định được cơ sở nguồn.');
        return;
      }
      if (!toStoreId) {
        message.error('Vui lòng chọn cơ sở nhận.');
        return;
      }
      if (toStoreId === fromStoreId) {
        message.error('Cơ sở nhận phải khác cơ sở gửi.');
        return;
      }

      const qty = Number(values.quantity || 0);
      if (!Number.isFinite(qty) || qty <= 0) {
        message.error('Số lượng chuyển phải > 0.');
        return;
      }

      const stock = resolveInventoryStockForItem(transferSourceItem);
      const lineQtyAbs = Math.abs(Number(transferSourceItem.quantity || 0));
      const maxTransfer = stock > 0 ? stock : lineQtyAbs;
      if (qty > maxTransfer) {
        message.error(
          `Không được chuyển vượt quá ${formatNumberVi(maxTransfer, 2)} ${transferSourceItem.unit || ''}` +
            (stock > 0 ? ' (theo tồn ước tính).' : ' (theo số lượng phiếu này).'),
        );
        return;
      }

      const fromName = stores.find((s) => getStoreOptionValue(s) === fromStoreId)?.name || fromStoreId;
      const toName = stores.find((s) => getStoreOptionValue(s) === toStoreId)?.name || toStoreId;

      setSaving(true);
      await purchaseService.addStoreTransfer({
        date: values.date.toDate(),
        itemName: transferSourceItem.itemName,
        unit: transferSourceItem.unit,
        quantity: qty,
        unitPrice: Number(transferSourceItem.unitPrice || 0),
        fromStoreId,
        fromStoreName: fromName,
        toStoreId,
        toStoreName: toName,
        notes: (values.notes || '').trim(),
        createdBy: currentUser?.userId || getCurrentUserId(),
      });

      purchaseListCacheRef.current.clear();
      closeTransferModal();
      message.success('Đã tạo phiếu chuyển hàng giữa cơ sở.');
      await Promise.all([
        loadPurchaseItems(periodFilter, selectedDate, true),
        allPurchaseItemsLoaded ? loadAllPurchaseItems(true) : Promise.resolve(),
      ]);
    } catch (error) {
      if ((error as any)?.errorFields) return;
      message.error('Không thể tạo phiếu chuyển hàng.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDataEntryRole = async (target: DataEntryUser, checked: boolean) => {
    try {
      await dataEntryUserService.updateUserByUserId(target.userId, {
        dataEntryRole: checked,
        updatedBy: currentUser?.userId || getCurrentUserId(),
      });
      message.success('Đã cập nhật quyền nhập liệu.');
      loadUsers();
    } catch (error) {
      message.error('Không thể cập nhật quyền người dùng.');
    }
  };

  const openMissingRecipeModal = (record: RecipeMissRow) => {
    const selectedDish = menuItems.find((item) => item.itemId === (record.itemId || ''));
    setSelectedMissingRecipe(record);
    quickRecipeForm.setFieldsValue({
      itemId: record.itemId || '',
      itemName: record.itemName || '',
      group: selectedDish?.itemGroup || '',
      instructions: '',
      ingredients: [{ name: '', hotAmount: 'x', icedAmount: 'x' }],
      optionAddons: [],
    });
    setQuickPasteText('');
    setMissingRecipeModalOpen(true);
  };

  const closeMissingRecipeModal = () => {
    setMissingRecipeModalOpen(false);
    setSelectedMissingRecipe(null);
    quickRecipeForm.resetFields();
    setQuickPasteText('');
  };

  const handleApplyQuickPasteIngredientsForMissingRecipe = () => {
    if (!quickPasteText.trim()) {
      message.warning('Vui lòng dán nội dung công thức trước');
      return;
    }

    const parsed = parseQuickPasteIngredients(quickPasteText, isQuickSingleAmountMode);
    if (!parsed.length) {
      message.warning(
        isQuickSingleAmountMode
          ? 'Không đọc được dữ liệu. Mỗi dòng cần có: Tên thành phần + Định lượng'
          : 'Không đọc được dữ liệu. Mỗi dòng cần có: Tên thành phần + Nóng + Đá'
      );
      return;
    }

    quickRecipeForm.setFieldsValue({ ingredients: parsed });
    message.success(`Đã dán nhanh ${parsed.length} thành phần`);
  };

  const handlePrefillOptionsForMissingRecipe = () => {
    if (!selectedQuickRecipeItemId) {
      message.warning('Thiếu mã món để lấy option.');
      return;
    }

    if (!currentDishOptions.length) {
      message.info('Món này chưa có option trong trang Quản lý tùy chọn.');
      return;
    }

    const existingAddons = (quickRecipeForm.getFieldValue('optionAddons') || []) as OptionAddonFormValues[];
    const existingMap = new Map(existingAddons.map((addon) => [addon.optionId, addon]));

    const baseIngredients = (quickRecipeForm.getFieldValue('ingredients') || []) as RecipeIngredient[];
    const beanKeywords = ['hat', 'hạt', 'shot', 'espresso', 'coffee', 'robusta', 'arabica', 'blend'];
    const beanIngredient =
      baseIngredients.find((ingredient) => {
        const name = (ingredient?.name || '').toLowerCase();
        return beanKeywords.some((keyword) => name.includes(keyword));
      }) || baseIngredients[0];

    const defaultHotAmount = (beanIngredient?.hotAmount || '').toString().trim() || 'x';
    const defaultIcedAmount = (beanIngredient?.icedAmount || '').toString().trim() || 'x';

    const mergedAddons: OptionAddonFormValues[] = currentDishOptions.map((optionItem) => {
      const existing = existingMap.get(optionItem.optionId);
      return (
        existing || {
          optionId: optionItem.optionId,
          optionName: optionItem.optionName,
          ingredients: [
            {
              name: optionItem.optionName,
              hotAmount: defaultHotAmount,
              icedAmount: defaultIcedAmount,
            },
          ],
        }
      );
    });

    quickRecipeForm.setFieldsValue({ optionAddons: mergedAddons });
    message.success(`Đã lấy ${mergedAddons.length} option theo món.`);
  };

  const handleCreateMissingRecipe = async () => {
    try {
      const values = await quickRecipeForm.validateFields();
      const cleanedIngredients = (values.ingredients || [])
        .map((item) => ({
          name: (item.name || '').trim(),
          hotAmount: (item.hotAmount || '').toString().trim() || 'x',
          icedAmount: isQuickSingleAmountMode
            ? (item.hotAmount || '').toString().trim() || 'x'
            : (item.icedAmount || '').toString().trim() || 'x',
        }))
        .filter((item) => Boolean(item.name));

      if (!cleanedIngredients.length) {
        message.warning('Cần ít nhất 1 thành phần hợp lệ để tạo công thức.');
        return;
      }

      const cleanedOptionAddons: OptionAddon[] = (values.optionAddons || [])
        .map((addon) => ({
          optionId: addon.optionId,
          optionName:
            addon.optionName ||
            selectableOptionItems.find((optionItem) => optionItem.optionId === addon.optionId)?.optionName ||
            addon.optionId,
          ingredients: (addon.ingredients || [])
            .map((item) => ({
              name: (item.name || '').trim(),
              hotAmount: (item.hotAmount || '').toString().trim() || 'x',
              icedAmount: isQuickSingleAmountMode
                ? (item.hotAmount || '').toString().trim() || 'x'
                : (item.icedAmount || '').toString().trim() || 'x',
            }))
            .filter((item) => Boolean(item.name)),
        }))
        .filter((addon) => addon.optionId && addon.ingredients.length > 0);

      setSavingMissingRecipe(true);
      await recipeService.saveRecipe({
        itemId: (values.itemId || '').trim(),
        itemName: (values.itemName || '').trim(),
        group: (values.group || '').trim(),
        instructions: (values.instructions || '').trim(),
        ingredients: cleanedIngredients,
        optionAddons: cleanedOptionAddons,
      });

      message.success('Đã tạo công thức cho món này.');
      closeMissingRecipeModal();
      await loadIngredientOutputData(true);
    } catch (error) {
      if ((error as any)?.errorFields) {
        return;
      }
      message.error('Không thể tạo công thức cho món này.');
    } finally {
      setSavingMissingRecipe(false);
    }
  };

  const showNormalizeResult = (result: NormalizePurchaseItemNamesResult) => {
    const topChanges = result.changedPairs
      .slice(0, 8)
      .map((item) => `${item.from} -> ${item.to} (${item.count})`)
      .join('\n');

    Modal.info({
      title: 'Kết quả chuẩn hóa tên nguyên liệu',
      width: 720,
      content: (
        <div>
          <p>Tổng bản ghi: {result.totalDocs}</p>
          <p>Số tên khác nhau trước chuẩn hóa: {result.uniqueNames}</p>
          <p>Số tên chuẩn sau chuẩn hóa: {result.canonicalNames}</p>
          <p>Số phiếu đã đổi tên: {result.renamedDocs}</p>
          {topChanges ? (
            <Input.TextArea
              value={topChanges}
              rows={8}
              readOnly
              style={{ marginTop: 8 }}
            />
          ) : null}
        </div>
      ),
    });
  };

  const handleNormalizeNames = () => {
    if (!canEditPurchase) {
      message.warning('Chỉ admin mới được chuẩn hóa dữ liệu tên nguyên liệu.');
      return;
    }

    Modal.confirm({
      title: 'Chuẩn hóa tên nguyên liệu trên Firebase?',
      content:
        'Hệ thống sẽ gom tên gần giống nhau (ngưỡng 90%), đồng thời xóa hậu tố tháng như t1..t12/thang 1..12 để đồng bộ tên chung. Thao tác này cập nhật trực tiếp dữ liệu.',
      okText: 'Chuẩn hóa',
      cancelText: 'Hủy',
      okButtonProps: { loading: normalizingNames },
      onOk: async () => {
        try {
          setNormalizingNames(true);
          const result = await purchaseService.normalizePurchaseItemNames(0.9);
          message.success(`Đã chuẩn hóa ${result.renamedDocs} phiếu nhập.`);
          showNormalizeResult(result);
          purchaseListCacheRef.current.clear();
          setAllPurchaseItemsLoaded(false);
          setOutputDataLoaded(false);
          await Promise.all([
            loadPurchaseItems(periodFilter, selectedDate, true),
            loadAllPurchaseItems(true),
            loadIngredientOutputData(true),
          ]);
        } catch (error) {
          message.error('Không thể chuẩn hóa tên nguyên liệu trên Firebase.');
        } finally {
          setNormalizingNames(false);
        }
      },
    });
  };

  const showNormalizeUnitsResult = (result: NormalizePurchaseUnitsResult) => {
    const topChanges = result.changedPairs
      .slice(0, 8)
      .map((item) => `${item.from} -> ${item.to} (${item.count})`)
      .join('\n');

    Modal.info({
      title: 'Kết quả chuẩn hóa đơn vị',
      width: 720,
      content: (
        <div>
          <p>Tổng bản ghi: {result.totalDocs}</p>
          <p>Số phiếu đã chuẩn hóa: {result.normalizedDocs}</p>
          <p>Phiếu đã quy khối lượng/thể tích về gam (g): {result.convertedToGramDocs}</p>
          {topChanges ? (
            <Input.TextArea
              value={topChanges}
              rows={8}
              readOnly
              style={{ marginTop: 8 }}
            />
          ) : null}
        </div>
      ),
    });
  };

  const handleNormalizeUnits = () => {
    if (!canEditPurchase) {
      message.warning('Chỉ admin mới được chuẩn hóa dữ liệu đơn vị.');
      return;
    }

    Modal.confirm({
      title: 'Chuẩn hóa đơn vị dữ liệu cũ trên Firebase?',
      content:
        'Hệ thống sẽ quy đổi về gam (g): kg → g, l/lít → 1000 g, ml → g (quy ước 1 ml ≈ 1 g). Số lượng và đơn giá đổi tương ứng để giữ nguyên thành tiền.',
      okText: 'Chuẩn hóa',
      cancelText: 'Hủy',
      okButtonProps: { loading: normalizingUnits },
      onOk: async () => {
        try {
          setNormalizingUnits(true);
          const result = await purchaseService.normalizePurchaseUnits();
          message.success(`Đã chuẩn hóa đơn vị cho ${result.normalizedDocs} phiếu nhập.`);
          showNormalizeUnitsResult(result);
          purchaseListCacheRef.current.clear();
          setAllPurchaseItemsLoaded(false);
          setOutputDataLoaded(false);
          await Promise.all([
            loadPurchaseItems(periodFilter, selectedDate, true),
            loadAllPurchaseItems(true),
            loadIngredientOutputData(true),
          ]);
        } catch (error) {
          message.error('Không thể chuẩn hóa đơn vị dữ liệu cũ trên Firebase.');
        } finally {
          setNormalizingUnits(false);
        }
      },
    });
  };

  const showNormalizeUnitPricesResult = (result: NormalizePurchaseUnitPricesResult) => {
    const topChanges = result.topMatches
      .slice(0, 8)
      .map((item) => `${item.name} → ${formatNumberVi(item.unitPrice)} (${item.count})`)
      .join('\n');

    Modal.info({
      title: 'Kết quả sửa đơn giá <= 1',
      width: 720,
      content: (
        <div>
          <p>Tổng bản ghi: {result.totalDocs}</p>
          <p>Số phiếu có đơn giá = 1: {result.targetDocs}</p>
          <p>Số phiếu đã cập nhật: {result.updatedDocs}</p>
          <p>Số phiếu chưa tìm thấy giá phù hợp: {result.unmatchedDocs}</p>
          {topChanges ? (
            <Input.TextArea
              value={topChanges}
              rows={8}
              readOnly
              style={{ marginTop: 8 }}
            />
          ) : null}
        </div>
      ),
    });
  };

  const handleNormalizeUnitPrices = () => {
    if (!canEditPurchase) {
      message.warning('Chỉ admin mới được sửa đơn giá dữ liệu cũ.');
      return;
    }

    Modal.confirm({
      title: 'Sửa đơn giá <= 1 trên Firebase?',
      content:
        'Hệ thống sẽ ưu tiên lấy giá chuẩn từ kho tổng; nếu không có sẽ lấy giá mới nhất theo đúng tên nguyên liệu (bỏ hậu tố tháng) có đơn giá > 1 để cập nhật lại. Thao tác này cập nhật trực tiếp dữ liệu.',
      okText: 'Cập nhật',
      cancelText: 'Hủy',
      okButtonProps: { loading: normalizingUnitPrices },
      onOk: async () => {
        try {
          setNormalizingUnitPrices(true);
          const result = await purchaseService.normalizePurchaseUnitPrices(1);
          message.success(`Đã cập nhật ${result.updatedDocs} phiếu nhập.`);
          showNormalizeUnitPricesResult(result);
          purchaseListCacheRef.current.clear();
          setAllPurchaseItemsLoaded(false);
          setOutputDataLoaded(false);
          await Promise.all([
            loadPurchaseItems(periodFilter, selectedDate, true),
            loadAllPurchaseItems(true),
            loadIngredientOutputData(true),
          ]);
        } catch (error) {
          message.error('Không thể sửa đơn giá dữ liệu cũ trên Firebase.');
        } finally {
          setNormalizingUnitPrices(false);
        }
      },
    });
  };

  const handleUploadInvoiceJsonForOutput = (file: File) => {
    setUploadingInvoiceJson(true);

    parseJsonToInvoiceNotifications(file)
      .then((parsedInvoices) => {
        if (!parsedInvoices.length) {
          Modal.warning({
            title: 'File chưa có dữ liệu hóa đơn',
            content: 'Không tìm thấy dòng dữ liệu hợp lệ trong file JSON.',
          });
          return null;
        }

        return invoiceNotificationService.batchUpsertFromExcel(parsedInvoices);
      })
      .then(async (result) => {
        if (!result) return;

        message.success(
          `Đã thêm ${result.added}, cập nhật ${result.updated} dòng hóa đơn${
            result.skipped > 0 ? `, bỏ qua ${result.skipped} dòng đã tồn tại` : ''
          }.`
        );

        if (result.errors.length > 0) {
          Modal.error({
            title: 'Có lỗi khi xử lý file JSON hóa đơn',
            width: 720,
            content: (
              <div>
                <p>Một số dòng chưa lưu được:</p>
                <ul>
                  {result.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            ),
          });
        }

        if (result.added > 0 || result.updated > 0) {
          setOutputDataLoaded(false);
          await loadIngredientOutputData(true);
        }
      })
      .catch((error) => {
        message.error(error?.message || 'Không thể đọc hoặc lưu file JSON hóa đơn.');
      })
      .finally(() => {
        if (isMountedRef.current) {
          setUploadingInvoiceJson(false);
        }
      });

    return false;
  };

  const purchaseColumns = [
    {
      title: 'Ngày',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value: Date) => dayjs(value).format('DD/MM/YYYY'),
    },
    {
      title: 'Mã',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Nhóm',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Nguồn nhà cung cấp',
      dataIndex: 'supplierSource',
      key: 'supplierSource',
      width: 180,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Tên nguyên liệu',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 220,
      render: (value: string, record: PurchaseItem) => {
        const isOut = (record as any).isTransferOut === true;
        const isIn = (record as any).isTransferIn === true;
        const transferToStoreId = (record as any).transferToStoreId;
        const transferFromStoreId = (record as any).transferFromStoreId;
        const toName = transferToStoreId ? storeNameMap[transferToStoreId] : undefined;
        const fromName = transferFromStoreId ? storeNameMap[transferFromStoreId] : undefined;

        return (
          <Space size={6} wrap>
            <span>{value}</span>
            {isOut && <Tag color="gold">Chuyển đi{toName ? ` → ${toName}` : ''}</Tag>}
            {isIn && <Tag color="blue">Nhận chuyển{fromName ? ` ← ${fromName}` : ''}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Số lượng',
      key: 'quantity',
      width: 120,
      render: (_: unknown, record: PurchaseItem) => `${record.quantity} ${record.unit}`,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('vi-VN'),
    },
    {
      title: 'Thành tiền',
      dataIndex: 'total',
      key: 'total',
      width: 140,
      align: 'right' as const,
      render: (value: number) => <b>{value.toLocaleString('vi-VN')}</b>,
    },
    {
      title: 'Cơ sở',
      dataIndex: 'storeLocationId',
      key: 'storeLocationId',
      width: 180,
      render: (_: string | undefined, record: PurchaseItem) => {
        const effectiveStoreId = resolveStoreIdForPurchaseItem(record);
        return storeNameMap[effectiveStoreId || ''] || '-';
      },
    },
    {
      title: 'Người nhập',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 140,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 220,
      render: (_: unknown, record: PurchaseItem) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditModal(record)}
            disabled={(record as any).isTransferOut === true || (record as any).isTransferIn === true}
          />
          <Button
            size="small"
            onClick={() => openTransferModal(record)}
            disabled={!canAccessDataEntry || (record as any).isTransferOut === true || (record as any).isTransferIn === true}
          >
            Chuyển bớt
          </Button>
          <Popconfirm
            title={(record as any).isTransferOut === true || (record as any).isTransferIn === true ? 'Hủy chuyển hàng' : 'Xóa phiếu nhập'}
            description={
              (record as any).isTransferOut === true || (record as any).isTransferIn === true
                ? 'Bạn có chắc muốn hủy chuyển? Hệ thống sẽ xóa cả phiếu chuyển đi và phiếu nhận.'
                : 'Bạn có chắc muốn xóa phiếu này?'
            }
            onConfirm={() => handleDelete(record)}
            okText={(record as any).isTransferOut === true || (record as any).isTransferIn === true ? 'Hủy chuyển' : 'Xóa'}
            cancelText="Hủy"
            disabled={!canEditPurchase}
          >
            <Button icon={<DeleteOutlined />} danger size="small" disabled={!canEditPurchase} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const purchaseSummaryColumns = [
    {
      title: 'Tên nguyên liệu chuẩn',
      dataIndex: 'ingredientName',
      key: 'ingredientName',
      width: 240,
      sorter: (a: PurchaseSummaryRow, b: PurchaseSummaryRow) =>
        (a.ingredientName || '').localeCompare(b.ingredientName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tên biến thể đã nhập',
      dataIndex: 'variantNames',
      key: 'variantNames',
      width: 320,
      render: (value: string[]) => (value?.length ? value.join(', ') : '-'),
    },
    {
      title: 'Đơn vị',
      dataIndex: 'unit',
      key: 'unit',
      width: 110,
      render: (value: string) => value || '-',
    },
    {
      title: 'Số lần nhập',
      dataIndex: 'entryCount',
      key: 'entryCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: PurchaseSummaryRow, b: PurchaseSummaryRow) =>
        (a.entryCount || 0) - (b.entryCount || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng số lượng',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 140,
      align: 'right' as const,
      sorter: (a: PurchaseSummaryRow, b: PurchaseSummaryRow) =>
        (a.totalQuantity || 0) - (b.totalQuantity || 0),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => Number(value || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Tổng thành tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 160,
      align: 'right' as const,
      sorter: (a: PurchaseSummaryRow, b: PurchaseSummaryRow) =>
        (a.totalAmount || 0) - (b.totalAmount || 0),
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
      render: (value: number) => <b>{Number(value || 0).toLocaleString('vi-VN')}</b>,
    },
  ];

  const roleColumns = [
    {
      title: 'Mã người dùng',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => value || '-',
    },
    {
      title: 'Admin',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      width: 110,
      render: (value: boolean) => (value ? <Tag color="green">Admin</Tag> : <Tag>Không</Tag>),
    },
    {
      title: 'Quyền nhập liệu',
      dataIndex: 'dataEntryRole',
      key: 'dataEntryRole',
      width: 160,
      render: (value: boolean, record: DataEntryUser) => (
        <Switch
          checked={Boolean(value)}
          disabled={Boolean(record.isAdmin)}
          onChange={(checked) => handleToggleDataEntryRole(record, checked)}
        />
      ),
    },
  ];

  const dataEntryContent = (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="none">
            <Avatar size={48} icon={<UserOutlined />} src={currentUser?.avatar || undefined} />
          </Col>
          <Col flex="auto">
            <div style={{ fontWeight: 600 }}>{currentUser?.name || 'Người dùng nhập liệu'}</div>
            <Space size={8} wrap>
              <Tag color={currentUser?.isAdmin ? 'green' : 'blue'}>
                {currentUser?.isAdmin ? 'Admin' : currentUser?.dataEntryRole ? 'Data Entry' : 'No Role'}
              </Tag>
              <span style={{ color: '#8c8c8c' }}>Admin mới có quyền sửa/xóa phiếu nhập</span>
            </Space>
          </Col>
        </Row>
      </Card>

      {!canAccessDataEntry ? (
        <Card>
          <Empty description="Bạn chưa được cấp quyền vào luồng nhập liệu." />
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={6}>
                <Select
                  style={{ width: '100%' }}
                  value={periodFilter}
                  onChange={(value) => setPeriodFilter(value)}
                  options={filterOptions}
                />
              </Col>
              <Col xs={24} md={6}>
                <DatePicker
                  style={{ width: '100%' }}
                  value={selectedDate}
                  onChange={(value) => setSelectedDate(value || dayjs())}
                  picker={
                    periodFilter === 'year'
                      ? 'year'
                      : periodFilter === 'month'
                      ? 'month'
                      : 'date'
                  }
                  disabled={periodFilter === 'all'}
                />
              </Col>
              <Col xs={24} md={6}>
                <Select
                  style={{ width: '100%' }}
                  value={storeFilter}
                  onChange={setStoreFilter}
                  options={[
                    { label: 'Tất cả cơ sở', value: 'all' },
                    ...stores
                      .filter((store) => !!getStoreOptionValue(store))
                      .map((store) => ({ label: store.name, value: getStoreOptionValue(store) })),
                  ]}
                />
              </Col>
              <Col xs={24} md={6}>
                <Input.Search
                  allowClear
                  placeholder="Tìm theo mã, nhóm, nguồn NCC, tên nguyên liệu"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
            </Row>

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Tổng phiếu" value={totalOrders} formatter={(v) => formatNumberVi(Number(v), 0)} />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Tổng tiền" value={totalAmount} formatter={(v) => formatNumberVi(Number(v), 0)} />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Số nguyên liệu"
                  value={purchaseSummaryRows.length}
                  formatter={(v) => formatNumberVi(Number(v), 0)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Tổng số lượng"
                  value={purchaseSummaryRows.reduce((sum, row) => sum + (row.totalQuantity || 0), 0)}
                  formatter={(v) => formatNumberVi(Number(v), 2)}
                />
              </Col>
            </Row>

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              <Col xs={24} md={24} style={{ textAlign: 'right' }}>
                <Space>
                  {canEditPurchase && !hideNormalizationActions && (
                    <Button loading={normalizingNames} onClick={handleNormalizeNames}>
                      Chuẩn hóa tên (90%)
                    </Button>
                  )}
                  {canEditPurchase && !hideNormalizationActions && (
                    <Button loading={normalizingUnits} onClick={handleNormalizeUnits}>
                      Chuẩn hóa đơn vị cũ
                    </Button>
                  )}
                  {canEditPurchase && !hideNormalizationActions && (
                    <Button loading={normalizingUnitPrices} onClick={handleNormalizeUnitPrices}>
                      Sửa đơn giá {'<= 1'}
                    </Button>
                  )}
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                  Thêm phiếu nhập
                </Button>
                <Button
                  icon={<DollarOutlined />}
                  onClick={async () => {
                    // Load expense catalog khi mở modal (lazy load)
                    try {
                      const catalog = await expenseCatalogService.getAll();
                      setExpenseCatalog(catalog);
                    } catch {}
                    expenseEntryForm.resetFields();
                    expenseEntryForm.setFieldsValue({ date: selectedDate });
                    setExpenseEntryModalOpen(true);
                  }}
                >
                  Nhập chi phí
                </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Card
            title="Tổng hợp nguyên liệu nhập theo bộ lọc"
            extra={<span style={{ color: '#8c8c8c' }}>Gom tên giống nhau để đồng bộ dữ liệu nhập</span>}
            style={{ marginBottom: 16 }}
          >
            <Table
              rowKey="key"
              columns={purchaseSummaryColumns}
              dataSource={purchaseSummaryRows}
              loading={loading}
              scroll={{ x: 1100 }}
              pagination={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                showSizeChanger: true,
                locale: { items_per_page: 'dòng/trang' },
              }}
              locale={{ emptyText: 'Không có dữ liệu nguyên liệu nhập theo bộ lọc hiện tại.' }}
            />
          </Card>

          <Table
            rowKey={(record) => record.id || `${record.itemName}-${record.createdAt}`}
            columns={purchaseColumns}
            dataSource={filteredItems}
            loading={loading}
            scroll={{ x: 1300 }}
            pagination={{
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              showSizeChanger: true,
              locale: { items_per_page: 'dòng/trang' },
            }}
          />

          {canEditPurchase && (
            <Card title="Quản lý quyền nhập liệu" style={{ marginTop: 16 }}>
              <Table
                rowKey={(record) => record.id || record.userId}
                columns={roleColumns}
                dataSource={users}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          )}
        </>
      )}

      {/* Modal nhập chi phí hàng ngày (tự tab Nguyên liệu nhập) */}
      <Modal
        title="Nhập chi phí hàng ngày"
        open={expenseEntryModalOpen}
        onCancel={() => {
          setExpenseEntryModalOpen(false);
          expenseEntryForm.resetFields();
        }}
        onOk={async () => {
          try {
            const values = await expenseEntryForm.validateFields();
            const dateStr = values.date.format('YYYY-MM-DD');
            const monthStr = values.date.format('YYYY-MM');
            const catalog = expenseCatalog.find((c) => c.id === values.expenseCatalogId);
            if (!catalog?.id) {
              message.error('Không tìm thấy danh mục chi phí.');
              return;
            }
            setExpenseSaving(true);
            await expenseEntryService.addEntry({
              date: dateStr,
              month: monthStr,
              expenseCatalogId: catalog.id,
              expenseName: catalog.name,
              unitPrice: Number(values.unitPrice),
              notes: (values.notes || '').trim(),
            });
            message.success('Đã lưu mục chi phí.');
            setExpenseEntryModalOpen(false);
            expenseEntryForm.resetFields();
          } catch (err: any) {
            if (err?.errorFields) return;
            message.error('Không thể lưu mục chi phí.');
          } finally {
            setExpenseSaving(false);
          }
        }}
        confirmLoading={expenseSaving}
        okText="Lưu"
        cancelText="Hủy"
        width={500}
        destroyOnClose
      >
        <Form form={expenseEntryForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="date" label="Ngày" rules={[{ required: true, message: 'Chọn ngày' }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expenseCatalogId" label="Loại chi phí" rules={[{ required: true, message: 'Chọn loại chi phí' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn từ danh mục chi phí..."
                  options={expenseCatalogOptions}
                  onChange={(catalogId) => {
                    const opt = expenseCatalogOptions.find((o) => o.value === catalogId);
                    if (opt?.defaultUnitPrice) {
                      expenseEntryForm.setFieldsValue({ unitPrice: opt.defaultUnitPrice });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {expenseSelectedOpt && expenseSelectedOpt.defaultUnitPrice > 0 && (
            <div
              style={{
                background: '#f0f5ff',
                border: '1px solid #adc6ff',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 12,
                fontSize: 13,
                color: '#2f54eb',
              }}
            >
              <DollarOutlined style={{ marginRight: 4 }} />
              <b>{expenseSelectedOpt.label}</b> — Giá mặc định:{' '}
              <b>{Number(expenseSelectedOpt.defaultUnitPrice).toLocaleString('vi-VN')} đ</b>
            </div>
          )}

          <Form.Item
            name="unitPrice"
            label="Số tiền (đ)"
            rules={[
              { required: true, message: 'Nhập số tiền' },
              { validator: (_, v) => Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')) },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              placeholder="Nhập số tiền chi phí (đ)"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingItem ? 'Sửa phiếu nhập nguyên liệu' : 'Thêm phiếu nhập nguyên liệu'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="Lưu"
        cancelText="Hủy"
        width={860}
      >
        <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
          {warehouseGoods.length === 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Chưa có danh mục hàng hóa kho tổng"
              description="Vào tab Kho tổng → Danh mục hàng hóa để dán/Excel hoặc thêm mặt hàng trước khi nhập nguyên liệu."
            />
          )}
          <Space size={8} wrap style={{ marginBottom: 12 }}>
            {quickButtons.slice(0, 8).map((button) => (
              <Button key={button.id || button.itemName} onClick={() => applyQuickButton(button)}>
                {button.itemName}
              </Button>
            ))}
          </Space>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="code" label="Mã hàng">
                <Input placeholder="VD: NL-001" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="category"
                label="Nhóm"
                rules={[{ required: true, message: 'Vui lòng chọn nhóm nguyên liệu' }]}
              >
                <Select
                  showSearch
                  placeholder="Chọn nhóm nguyên liệu"
                  optionFilterProp="label"
                  options={purchaseCategoryOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="date"
                label="Ngày nhập"
                rules={[{ required: true, message: 'Vui lòng chọn ngày nhập' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                name="supplierId"
                label="Nhà cung cấp"
                rules={[{ required: true, message: 'Vui lòng chọn nhà cung cấp' }]}
              >
                <Select
                  showSearch
                  placeholder="Chọn nhà cung cấp"
                  optionFilterProp="label"
                  options={suppliers.map((supplier) => ({
                    value: supplier.id,
                    label: `${supplier.code} - ${supplier.name}`,
                    supplier,
                  }))}
                  onChange={(_, option: any) => form.setFieldValue('supplierSource', option?.supplier?.name)}
                  dropdownRender={(menu) => <>{menu}<Button type="link" icon={<PlusOutlined />} onClick={() => setSupplierModalOpen(true)}>Thêm nhà cung cấp</Button></>}
                />
              </Form.Item>
              <Form.Item name="supplierSource" hidden><Input /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="itemName"
                label="Tên nguyên liệu"
                rules={[
                  { required: true, message: 'Vui lòng chọn nguyên liệu' },
                  {
                    validator: () => {
                      const id = form.getFieldValue('warehouseCoffeeId');
                      if (!id || !warehouseGoods.some((g) => g.id === id)) {
                        return Promise.reject(new Error('Chỉ được chọn hàng trong danh mục hàng hóa kho tổng'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra="Chỉ chọn từ danh mục hàng hóa đã khai báo ở Kho tổng"
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Chọn hàng từ danh mục kho tổng..."
                  optionFilterProp="label"
                  options={warehouseGoodsSelectOptions}
                  onChange={(value, option: any) => {
                    if (value == null || value === '') {
                      form.setFieldsValue({ itemName: undefined, warehouseCoffeeId: undefined });
                      return;
                    }
                    const oid = option?.id as string | undefined;
                    const defaults = getWarehouseDefaultsById(oid);
                    const currentValues = form.getFieldsValue();
                    const nextUnit = defaults?.unit || currentValues.unit;
                    const nextUnitPrice =
                      defaults && defaults.unitPrice !== undefined ? defaults.unitPrice : currentValues.unitPrice;
                    const quantity = Number(currentValues.quantity || 0);
                    form.setFieldsValue({
                      itemName: value,
                      warehouseCoffeeId: oid,
                      ...(nextUnit ? { unit: nextUnit } : {}),
                      ...(nextUnitPrice !== undefined ? { unitPrice: nextUnitPrice } : {}),
                      total:
                        nextUnitPrice !== undefined
                          ? Number(quantity) * Number(nextUnitPrice || 0)
                          : currentValues.total,
                    });
                    if (oid) {
                      applyRecentDefaultsByItemName(String(value));
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name="warehouseCoffeeId" hidden>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="storeLocationId"
                label="Cơ sở"
                rules={
                  editingItem
                    ? []
                    : [{ required: true, message: 'Vui lòng chọn cơ sở nhập nguyên liệu' }]
                }
              >
                <Select
                  allowClear={Boolean(editingItem)}
                  placeholder="Chọn cơ sở"
                  options={stores
                    .filter((store) => !!getStoreOptionValue(store))
                    .map((store) => ({ label: store.name, value: getStoreOptionValue(store) }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                name="quantity"
                label="Số lượng"
                rules={[
                  { required: true, message: 'Vui lòng nhập số lượng' },
                  {
                    validator: (_, value) =>
                      Number(value) > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('Số lượng phải lớn hơn 0')),
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="unit"
                label="Đơn vị"
                extra="Tự chuẩn hóa khi lưu: kg → g; l/lít → 1000 g; ml → g (≈1 ml = 1 g)"
                rules={[{ required: true, message: 'Vui lòng nhập đơn vị' }]}
              >
                <Input placeholder="kg, gói, chai..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="unitPrice"
                label="Đơn giá"
                rules={[
                  { required: true, message: 'Vui lòng nhập đơn giá' },
                  {
                    validator: (_, value) =>
                      Number(value) > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('Đơn giá phải lớn hơn 0')),
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="total" label="Thành tiền">
                <InputNumber style={{ width: '100%' }} readOnly />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="description" label="Mô tả">
                <Input.TextArea rows={2} placeholder="Ghi chú thêm" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Kho tổng linkage ── */}
          <div style={{
            background: fromWarehouse ? '#e6f7ff' : '#fafafa',
            border: `1px solid ${fromWarehouse ? '#91caff' : '#d9d9d9'}`,
            borderRadius: 6,
            padding: '12px 16px',
            marginTop: 4,
          }}>
            {isCoffeeCategory ? (
              // Khi nhóm là cà phê: hiển thị thông tin cố định, không cần Switch
              <Space align="center">
                <Tag color="blue" style={{ margin: 0 }}>Lấy từ kho tổng</Tag>
                <span style={{ color: '#595959', fontSize: 13 }}>
                  Nhóm cà phê: chỉ chọn hàng trong danh mục hàng hóa kho tổng; lưu sẽ trừ tồn kho tổng
                </span>
              </Space>
            ) : (
              <>
                <Space align="center" style={{ marginBottom: 0 }}>
                  <Switch
                    checked={fromWarehouse}
                    onChange={(checked) => setFromWarehouse(checked)}
                    size="small"
                  />
                  <span style={{ fontWeight: 500 }}>Lấy từ kho tổng</span>
                  {fromWarehouse && (
                    <Tag color="blue">Sẽ trừ tồn kho tổng khi lưu</Tag>
                  )}
                </Space>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 8 }}>
                  Hàng đã chọn ở trên phải có trong danh mục; bật công tắc này để đồng thời ghi xuất kho tổng.
                </div>
              </>
            )}
          </div>
        </Form>
      </Modal>

      <Modal
        title="Thêm nhà cung cấp"
        open={supplierModalOpen}
        onCancel={() => setSupplierModalOpen(false)}
        onOk={() => void createSupplier()}
        confirmLoading={supplierSaving}
        okText="Thêm"
        cancelText="Hủy"
        destroyOnClose
      >
        <Form form={supplierForm} layout="vertical">
          <Form.Item name="code" label="Mã nhà cung cấp" rules={[{ required: true, message: 'Nhập mã nhà cung cấp' }]}><Input /></Form.Item>
          <Form.Item name="name" label="Tên nhà cung cấp" rules={[{ required: true, message: 'Nhập tên nhà cung cấp' }]}><Input /></Form.Item>
          <Form.Item name="phone" label="Số điện thoại"><Input /></Form.Item>
          <Form.Item name="address" label="Địa chỉ"><Input /></Form.Item>
          <Form.Item name="note" label="Ghi chú"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Chuyển nguyên liệu sang cơ sở khác"
        open={transferModalOpen}
        onCancel={closeTransferModal}
        onOk={handleSubmitTransfer}
        confirmLoading={saving}
        okText="Tạo chuyển"
        cancelText="Hủy"
        width={720}
        destroyOnClose
      >
        <Form form={transferForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Nguyên liệu">
                <Input value={transferSourceItem?.itemName || ''} readOnly />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Đơn vị">
                <Input value={transferSourceItem?.unit || ''} readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Cơ sở gửi">
                <Input
                  value={
                    transferSourceItem
                      ? storeNameMap[resolveStoreIdForPurchaseItem(transferSourceItem) || ''] || '-'
                      : ''
                  }
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Số lượng trên phiếu (giống form sửa)">
                <Input
                  value={
                    transferSourceItem
                      ? `${formatNumberVi(Math.abs(Number(transferSourceItem.quantity || 0)), 2)} ${transferSourceItem.unit || ''}`
                      : '-'
                  }
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Đơn giá">
                <Input
                  value={
                    transferSourceItem
                      ? formatNumberVi(Number(transferSourceItem.unitPrice || 0), 0)
                      : '-'
                  }
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Thành tiền phiếu">
                <Input
                  value={
                    transferSourceItem ? formatNumberVi(Number(transferSourceItem.total || 0), 0) : '-'
                  }
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Tồn ước tính (nếu có)">
                <Input
                  value={
                    !transferSourceItem
                      ? '-'
                      : (() => {
                          const s = resolveInventoryStockForItem(transferSourceItem);
                          return s > 0
                            ? `${formatNumberVi(s, 2)} ${transferSourceItem.unit || ''}`
                            : '—';
                        })()
                  }
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="date" label="Ngày chuyển" rules={[{ required: true, message: 'Chọn ngày chuyển' }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="toStoreId"
                label="Cơ sở nhận"
                rules={[
                  { required: true, message: 'Chọn cơ sở nhận' },
                  {
                    validator: (_, v) => {
                      if (!transferSourceItem) return Promise.resolve();
                      const fromStoreId = resolveStoreIdForPurchaseItem(transferSourceItem);
                      if (!v || !fromStoreId) return Promise.resolve();
                      return String(v) === String(fromStoreId)
                        ? Promise.reject(new Error('Cơ sở nhận phải khác cơ sở gửi'))
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn cơ sở nhận"
                  options={stores
                    .filter((store) => !!getStoreOptionValue(store))
                    .map((store) => ({ label: store.name, value: getStoreOptionValue(store) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="quantity"
                label="Số lượng chuyển"
                rules={[
                  { required: true, message: 'Nhập số lượng chuyển' },
                  {
                    validator: (_, v) => {
                      const qty = Number(v || 0);
                      if (!Number.isFinite(qty) || qty <= 0) return Promise.reject(new Error('Phải > 0'));
                      if (!transferSourceItem) return Promise.resolve();
                      const stock = resolveInventoryStockForItem(transferSourceItem);
                      return qty <= stock
                        ? Promise.resolve()
                        : Promise.reject(new Error(`Không đủ tồn (tồn hiện tại: ${formatNumberVi(stock, 2)})`));
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  const tabsContent = (
    <>
      {!embedded && <h2 style={{ marginTop: 16, marginBottom: 12 }}>Quản lý kho</h2>}
      <Tabs
        defaultActiveKey={initialTabKey}
        onChange={(activeKey) => {
          if (Array.isArray(allowedTabKeys) && allowedTabKeys.length > 0 && !allowedTabKeys.includes(activeKey)) {
            return;
          }

          if (activeKey === 'kho-tong') {
            setKhoTongReloadToken((t) => t + 1);
          }
          if (activeKey === 'partner-coffee-management') {
            setPartnerTabReloadToken((t) => t + 1);
          }
          if (activeKey === 'purchase-input') {
            loadPurchaseItems(periodFilter, selectedDate, true);
            loadWarehouseGoodsCatalog(true);
          }
          if (activeKey === 'ingredient-output') {
            if (!outputDataLoaded) loadIngredientOutputData();
            if (!allPurchaseItemsLoaded) loadAllPurchaseItems();
          } else if (activeKey === 'ingredient-inventory') {
            if (!outputDataLoaded) loadIngredientOutputData();
            if (!coffeeTransactionsLoaded) loadCoffeeTransactions();
            if (!allPurchaseItemsLoaded) loadAllPurchaseItems();
          }
        }}
        items={[
          {
            key: 'kho-tong',
            label: 'Kho tổng',
            children: <KhoTongSection reloadToken={khoTongReloadToken} />,
          },
          {
            key: 'purchase-input',
            label: 'Nguyên liệu nhập',
            children: dataEntryContent,
          },
          {
            key: 'ingredient-output',
            label: 'Nguyên liệu xuất',
            children: (
              <>
                {invoiceSourceWarnings.length > 0 && (
                  <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    message="Dữ liệu hóa đơn chưa đầy đủ"
                    description={invoiceSourceWarnings.join(' ')}
                  />
                )}

                <Card style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={6}>
                      <Select
                        style={{ width: '100%' }}
                        value={outputPeriodFilter}
                        onChange={(value) => setOutputPeriodFilter(value)}
                        options={filterOptions}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={outputSelectedDate}
                        onChange={(value) => setOutputSelectedDate(value || dayjs())}
                        picker={
                          outputPeriodFilter === 'year'
                            ? 'year'
                            : outputPeriodFilter === 'month'
                            ? 'month'
                            : 'date'
                        }
                        disabled={outputPeriodFilter === 'all'}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Select
                        style={{ width: '100%' }}
                        value={outputStoreFilter}
                        onChange={setOutputStoreFilter}
                        options={outputStoreOptions}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Input.Search
                        allowClear
                        placeholder="Tìm theo mã/tên món hóa đơn"
                        value={outputSearchTerm}
                        onChange={(e) => setOutputSearchTerm(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    <Col xs={24} md={12}>
                      <Upload
                        accept=".json,application/json"
                        showUploadList={false}
                        beforeUpload={handleUploadInvoiceJsonForOutput}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploadingInvoiceJson}
                        >
                          Upload file JSON hóa đơn
                        </Button>
                      </Upload>
                    </Col>
                  </Row>

                  <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="Dòng hóa đơn đã tính"
                        value={ingredientOutputData.sourceRowCount}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="Số nguyên liệu có phát sinh"
                        value={ingredientOutputData.ingredientRows.length}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic
                        title="Số nhóm nguyên liệu / đơn vị"
                        value={ingredientOutputData.ingredientRows.length}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                  </Row>
                </Card>

                {ingredientOutputData.missingRows.length > 0 && (
                  <Alert
                    style={{ marginBottom: 16 }}
                    type="info"
                    showIcon
                    message={`Có ${ingredientOutputData.missingRows.length} món chưa có công thức nên chưa được tính xuất nguyên liệu. Hãy bổ sung công thức để kho được trừ chính xác.`}
                  />
                )}

                <Card title="Tổng hợp nguyên liệu đã lấy" style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12}>
                      <Input.Search
                        allowClear
                        placeholder="Tìm kiếm theo tên nguyên liệu"
                        value={ingredientSearchTerm}
                        onChange={(e) => setIngredientSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                      <span style={{ color: '#8c8c8c' }}>
                        Số nguyên liệu đang hiển thị: {filteredIngredientRows.length}
                      </span>
                    </Col>
                  </Row>

                  <Table
                    rowKey="key"
                    columns={ingredientOutputColumns}
                    dataSource={filteredIngredientRows}
                    loading={outputLoading}
                    pagination={{ pageSize: 12 }}
                    scroll={{ x: 760 }}
                    locale={{ emptyText: 'Không có dữ liệu nguyên liệu sử dụng theo bộ lọc hiện tại.' }}
                  />
                </Card>

                <Card
                  title="Danh sách hóa đơn kiểm tra công thức"
                  extra="Bấm vào 1 hóa đơn để xem chi tiết cách tính nguyên liệu"
                  style={{ marginBottom: 16 }}
                >
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12}>
                      <Input.Search
                        allowClear
                        placeholder="Lọc riêng danh sách hóa đơn theo tên món"
                        value={invoiceItemSearchTerm}
                        onChange={(e) => setInvoiceItemSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                      <span style={{ color: '#8c8c8c' }}>
                        Tổng hóa đơn đang hiển thị: {outputInvoiceGroups.length}
                      </span>
                    </Col>
                  </Row>

                  <Table
                    rowKey="key"
                    columns={invoiceGroupColumns}
                    dataSource={outputInvoiceGroups}
                    loading={outputLoading}
                    pagination={{
                      current: invoiceTablePage,
                      pageSize: invoiceTablePageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ['8', '20', '50', '100'],
                      onChange: (page, size) => {
                        setInvoiceTablePage(page);
                        if (size !== invoiceTablePageSize) {
                          setInvoiceTablePageSize(size);
                          setInvoiceTablePage(1);
                        }
                      },
                    }}
                    scroll={{ x: 1500 }}
                    onRow={(record) => ({
                      onClick: () => {
                        setSelectedInvoiceGroup(record);
                        setFormulaModalOpen(true);
                      },
                      style: { cursor: 'pointer' },
                    })}
                    locale={{ emptyText: 'Không có hóa đơn theo bộ lọc hiện tại.' }}
                  />
                </Card>

                <Card title="Món chưa có công thức" extra="Các món trong danh sách này cần tạo công thức để tính chính xác">
                  <Table
                    rowKey="key"
                    columns={missingRecipeColumns}
                    dataSource={ingredientOutputData.missingRows}
                    loading={outputLoading}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: 760 }}
                    onRow={(record) => ({
                      onClick: () => openMissingRecipeModal(record),
                      style: { cursor: 'pointer' },
                    })}
                    locale={{ emptyText: 'Tất cả món trong bộ lọc đã có công thức.' }}
                  />
                </Card>

                <Modal
                  title={`Chi tiết công thức tính nguyên liệu - HĐ ${selectedInvoiceGroup?.invoiceCode || '-'}`}
                  open={formulaModalOpen}
                  onCancel={() => setFormulaModalOpen(false)}
                  footer={null}
                  width={1200}
                >
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={6}>
                      <Statistic title="Mã HĐ" value={selectedInvoiceGroup?.invoiceCode || '-'} />
                    </Col>
                    <Col xs={24} md={6}>
                      <Statistic title="Ngày giờ" value={`${selectedInvoiceGroup?.checkInDate || '-'} ${selectedInvoiceGroup?.checkInTime || ''}`} />
                    </Col>
                    <Col xs={24} md={6}>
                      <Statistic
                        title="Số dòng món"
                        value={selectedInvoiceGroup?.lineCount || 0}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Statistic
                        title="Số dòng nguyên liệu"
                        value={selectedInvoiceFormulaData.ingredientRows.length}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                  </Row>

                  {selectedInvoiceFormulaData.missingCount > 0 && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type="warning"
                      showIcon
                      message={`Hóa đơn này có ${selectedInvoiceFormulaData.missingCount} dòng món chưa có công thức.`}
                    />
                  )}
                  {selectedInvoiceFormulaData.optionWarnings.length > 0 && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type="warning"
                      showIcon
                      message="Option có thể ảnh hưởng định lượng nhưng chưa có nguyên liệu cộng thêm"
                      description={selectedInvoiceFormulaData.optionWarnings.join(' ')}
                    />
                  )}

                  <Card size="small" title="Tổng quan theo từng dòng món" style={{ marginBottom: 12 }}>
                    <Table
                      rowKey="key"
                      columns={formulaLineColumns}
                      dataSource={selectedInvoiceFormulaData.lineRows}
                      pagination={false}
                      scroll={{ x: 900 }}
                    />
                  </Card>

                  <Card size="small" title="Chi tiết công thức tính nguyên liệu">
                    <Table
                      rowKey="key"
                      columns={formulaIngredientColumns}
                      dataSource={selectedInvoiceFormulaData.ingredientRows}
                      pagination={{ pageSize: 12 }}
                      scroll={{ x: 900, y: 360 }}
                    />
                  </Card>
                </Modal>

                <Modal
                  title={`Tạo công thức nhanh - ${selectedMissingRecipe?.itemName || ''}`}
                  open={missingRecipeModalOpen}
                  onCancel={closeMissingRecipeModal}
                  onOk={handleCreateMissingRecipe}
                  confirmLoading={savingMissingRecipe}
                  okText="Lưu công thức"
                  cancelText="Hủy"
                  width={980}
                  destroyOnClose
                >
                  <Form form={quickRecipeForm} layout="vertical">
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item name="itemId" label="Mã món" rules={[{ required: true, message: 'Thiếu mã món' }]}>
                          <Input readOnly />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="itemName" label="Tên món" rules={[{ required: true, message: 'Thiếu tên món' }]}>
                          <Input readOnly />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col xs={24} md={8}>
                        <Form.Item name="group" label="Nhóm đồ">
                          <Select
                            showSearch
                            allowClear
                            placeholder="Arabica, Robusta, Cold brew..."
                            options={recipeGroupOptions}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={16}>
                        <Form.Item name="instructions" label="Ghi chú">
                          <Input.TextArea rows={2} placeholder="Ghi chú cách pha chế (nếu có)" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div
                      style={{
                        background: '#fafafa',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 8,
                        padding: 10,
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Dán nhanh công thức</div>
                      <Input.TextArea
                        rows={3}
                        value={quickPasteText}
                        onChange={(e) => setQuickPasteText(e.target.value)}
                        placeholder={
                          isQuickSingleAmountMode
                            ? 'Mỗi dòng: Tên thành phần[TAB]Định lượng\nVí dụ: Double shot A\t20\nSữa tươi\t190'
                            : 'Mỗi dòng: Tên thành phần[TAB]Nóng[TAB]Đá\nVí dụ: Double shot A\t20\t20\nSữa tươi\t180\t150'
                        }
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                        <span style={{ fontSize: 11, color: '#888' }}>
                          Hỗ trợ ngăn cách bằng tab, dấu ;, |, dấu phẩy, hoặc nhiều khoảng trắng.
                        </span>
                        <Space>
                          <Button size="small" onClick={() => setQuickPasteText('')}>
                            Xóa nội dung dán
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            onClick={handleApplyQuickPasteIngredientsForMissingRecipe}
                          >
                            Áp dụng vào thành phần
                          </Button>
                        </Space>
                      </div>
                    </div>

                    <Form.List name="ingredients">
                      {(fields, { add, remove }) => (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <b>Thành phần công thức</b>
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() => add({ name: '', hotAmount: 'x', icedAmount: 'x' })}
                            >
                              Thêm thành phần
                            </Button>
                          </div>

                          {fields.length > 0 && (
                            <Row gutter={8} style={{ marginBottom: 4, fontWeight: 600, fontSize: 12, color: '#666' }}>
                              <Col xs={24} md={isQuickSingleAmountMode ? 16 : 10}>Tên thành phần</Col>
                              <Col xs={22} md={6}>{isQuickSingleAmountMode ? 'Định lượng (g)' : 'Nóng (g)'}</Col>
                              {!isQuickSingleAmountMode && <Col xs={11} md={6}>Đá (g)</Col>}
                              <Col xs={2} md={2} />
                            </Row>
                          )}

                          {!fields.length ? (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="Chưa có thành phần nào. Bấm Thêm thành phần để bắt đầu."
                            />
                          ) : null}

                          {fields.map((field) => (
                            <Row gutter={8} key={field.key} style={{ marginBottom: 8 }}>
                              <Col xs={24} md={isQuickSingleAmountMode ? 16 : 10}>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'name']}
                                  style={{ marginBottom: 0 }}
                                  rules={[{ required: true, message: 'Nhập tên thành phần' }]}
                                >
                                  <Input placeholder="VD: Hạt robusta" />
                                </Form.Item>
                              </Col>
                              <Col xs={22} md={6}>
                                <Form.Item {...field} name={[field.name, 'hotAmount']} style={{ marginBottom: 0 }}>
                                  <Input
                                    placeholder={isQuickSingleAmountMode ? 'VD: 18, 18ml, 0.5l' : '18g / 18ml hoặc x'}
                                  />
                                </Form.Item>
                              </Col>
                              {!isQuickSingleAmountMode && (
                                <Col xs={11} md={6}>
                                  <Form.Item {...field} name={[field.name, 'icedAmount']} style={{ marginBottom: 0 }}>
                                    <Input placeholder="150g / 150ml hoặc x" />
                                  </Form.Item>
                                </Col>
                              )}
                              <Col xs={2} md={2} style={{ textAlign: 'right' }}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                    </Form.List>

                    <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 12 }}>
                      <Form.List name="optionAddons">
                        {(addonFields, { add: addAddon, remove: removeAddon }) => (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <b>Thành phần bổ sung theo option</b>
                              <Space>
                                <Button onClick={handlePrefillOptionsForMissingRecipe}>Lấy option của món</Button>
                                <Button
                                  type="dashed"
                                  icon={<PlusOutlined />}
                                  onClick={() => addAddon({ optionId: '', optionName: '', ingredients: [] })}
                                >
                                  Thêm option
                                </Button>
                              </Space>
                            </div>

                            {!addonFields.length && (
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Chưa có option nào. Bấm Thêm option nếu món có lựa chọn bổ sung."
                              />
                            )}

                            {addonFields.map((addonField) => (
                              <div
                                key={addonField.key}
                                style={{
                                  background: '#fafafa',
                                  border: '1px solid #e8e8e8',
                                  borderRadius: 6,
                                  padding: '8px 12px',
                                  marginBottom: 8,
                                }}
                              >
                                <Row gutter={8} align="middle" style={{ marginBottom: 6 }}>
                                  <Col flex="auto">
                                    <Form.Item
                                      {...addonField}
                                      name={[addonField.name, 'optionId']}
                                      style={{ marginBottom: 0 }}
                                      rules={[{ required: true, message: 'Chọn option' }]}
                                    >
                                      <Select
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        placeholder="Chọn option theo món"
                                        options={selectableOptionItems.map((optionItem) => ({
                                          value: optionItem.optionId,
                                          label: `${optionItem.optionName} (${optionItem.optionId})`,
                                        }))}
                                        onChange={(value) => {
                                          const found = selectableOptionItems.find((optionItem) => optionItem.optionId === value);
                                          if (found) {
                                            const current =
                                              (quickRecipeForm.getFieldValue('optionAddons') || []) as OptionAddonFormValues[];
                                            if (!current[addonField.name]) return;
                                            current[addonField.name].optionName = found.optionName;
                                            quickRecipeForm.setFieldsValue({ optionAddons: current });
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col flex="none">
                                    <Button
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      onClick={() => removeAddon(addonField.name)}
                                    >
                                      Xóa
                                    </Button>
                                  </Col>
                                </Row>

                                <Form.List name={[addonField.name, 'ingredients']}>
                                  {(ingFields, { add: addIng, remove: removeIng }) => (
                                    <>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                        <span style={{ fontSize: 11, color: '#888' }}>Nguyên liệu cộng thêm</span>
                                        <Button
                                          size="small"
                                          type="dashed"
                                          icon={<PlusOutlined />}
                                          onClick={() => addIng({ name: '', hotAmount: '', icedAmount: '' })}
                                        >
                                          + Nguyên liệu
                                        </Button>
                                      </div>

                                      {ingFields.length > 0 && (
                                        <Row gutter={6} style={{ marginBottom: 2, fontWeight: 600, fontSize: 11, color: '#aaa' }}>
                                          <Col md={isQuickSingleAmountMode ? 16 : 10}>Tên nguyên liệu</Col>
                                          <Col md={6}>{isQuickSingleAmountMode ? 'Định lượng (g)' : 'Nóng (g)'}</Col>
                                          {!isQuickSingleAmountMode && <Col md={6}>Đá (g)</Col>}
                                          <Col md={2} />
                                        </Row>
                                      )}

                                      {!ingFields.length && (
                                        <div style={{ fontSize: 11, color: '#bbb', marginBottom: 2 }}>Chưa có nguyên liệu</div>
                                      )}

                                      {ingFields.map((ingField) => (
                                        <Row gutter={6} key={ingField.key} align="middle" style={{ marginBottom: 4 }}>
                                          <Col xs={24} md={isQuickSingleAmountMode ? 16 : 10}>
                                            <Form.Item
                                              {...ingField}
                                              name={[ingField.name, 'name']}
                                              style={{ marginBottom: 0 }}
                                              rules={[{ required: true, message: 'Nhập tên' }]}
                                            >
                                              <Input placeholder="Tên nguyên liệu..." />
                                            </Form.Item>
                                          </Col>
                                          <Col xs={22} md={6}>
                                            <Form.Item {...ingField} name={[ingField.name, 'hotAmount']} style={{ marginBottom: 0 }}>
                                              <Input
                                    placeholder={isQuickSingleAmountMode ? 'VD: 18, 18ml, 0.5l' : '18g / 18ml hoặc x'}
                                  />
                                            </Form.Item>
                                          </Col>
                                          {!isQuickSingleAmountMode && (
                                            <Col xs={11} md={6}>
                                              <Form.Item {...ingField} name={[ingField.name, 'icedAmount']} style={{ marginBottom: 0 }}>
                                                <Input placeholder="150g / 150ml hoặc x" />
                                              </Form.Item>
                                            </Col>
                                          )}
                                          <Col xs={2} md={2} style={{ textAlign: 'center' }}>
                                            <Button danger icon={<DeleteOutlined />} onClick={() => removeIng(ingField.name)} />
                                          </Col>
                                        </Row>
                                      ))}
                                    </>
                                  )}
                                </Form.List>
                              </div>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </div>
                  </Form>
                </Modal>
              </>
            ),
          },
          {
            key: 'ingredient-inventory',
            label: 'Nguyên liệu tồn',
            children: (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={6}>
                      <Select
                        style={{ width: '100%' }}
                        value={inventoryPeriodFilter}
                        onChange={(value) => setInventoryPeriodFilter(value)}
                        options={filterOptions}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={inventorySelectedDate}
                        onChange={(value) => setInventorySelectedDate(value || dayjs())}
                        picker={
                          inventoryPeriodFilter === 'year'
                            ? 'year'
                            : inventoryPeriodFilter === 'month'
                            ? 'month'
                            : 'date'
                        }
                        disabled={inventoryPeriodFilter === 'all'}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Select
                        style={{ width: '100%' }}
                        value={inventoryStoreFilter}
                        onChange={setInventoryStoreFilter}
                        options={[
                          { label: 'Tất cả cơ sở', value: 'all' },
                          ...stores
                            .filter((store) => !!getStoreOptionValue(store))
                            .map((store) => ({ label: store.name, value: getStoreOptionValue(store) })),
                        ]}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Input.Search
                        allowClear
                        placeholder="Tìm theo tên nguyên liệu"
                        value={inventorySearchTerm}
                        onChange={(e) => setInventorySearchTerm(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    <Col xs={24} sm={12} md={4}>
                      <Statistic title="Nguyên liệu" value={inventoryDisplayRows.length} formatter={(v) => formatNumberVi(Number(v), 0)} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                      <Statistic
                        title="Lượt nhập"
                        value={inventoryStats.importCount}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                      <Statistic
                        title="Giá trị nhập lũy kế"
                        value={inventoryStats.totalImportedAmount}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                      <Statistic
                        title="Lượt chưa ghép"
                        value={inventoryMatchStats.unmatchedMatches}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                      <Statistic
                        title="Giá trị tồn ước tính"
                        value={inventoryStats.totalEstimatedStockAmount}
                        formatter={(v) => formatNumberVi(Number(v), 0)}
                      />
                    </Col>
                  </Row>
                </Card>

                <Alert
                  style={{ marginBottom: 16 }}
                  type="info"
                  showIcon
                  message="Tồn lũy kế đến cuối kỳ = Tổng nhập và điều chuyển đến cuối kỳ - Tổng lấy theo công thức món - Tổng xuất đối tác, tách riêng theo từng cơ sở."
                />

                {inventoryMissingRecipeCount > 0 && (
                  <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    message={`${formatNumberVi(inventoryMissingRecipeCount, 0)} món chưa có công thức không bị tự động trừ kho. Hãy bổ sung công thức để phân rã đúng thành nguyên liệu thực tế.`}
                  />
                )}

                {inventoryMatchStats.unmatchedRows > 0 && (
                  <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    message={`Chưa ghép được nguyên liệu nhập tương thích trong cùng cơ sở: ${formatNumberVi(inventoryMatchStats.unmatchedRows, 0)} dòng (${formatNumberVi(
                      inventoryMatchStats.unmatchedMatches,
                      0,
                    )} lượt). Vui lòng kiểm tra tên và đơn vị; hệ thống không tự quy đổi giữa khối lượng/thể tích với Hộp, Bịch hoặc Cái.`}
                  />
                )}

                <Table
                  rowKey="key"
                  columns={inventoryColumns}
                  dataSource={inventoryDisplayRows}
                  loading={loading || outputLoading}
                  scroll={{ x: 1250 }}
                  pagination={{ pageSize: 12 }}
                  locale={{ emptyText: 'Không có dữ liệu tồn theo bộ lọc hiện tại.' }}
                />

                <Modal
                  title={`Chi tiết tồn kho - ${selectedInventoryRow?.ingredientName || '-'}`}
                  open={inventoryDetailModalOpen}
                  onCancel={() => {
                    setInventoryDetailModalOpen(false);
                    setSelectedInventoryRow(null);
                  }}
                  footer={null}
                  width={1300}
                >
                  {selectedInventoryRow && (
                    <>
                      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                        <Col xs={24} md={6}>
                          <Statistic title="SL nhập" value={selectedInventoryRow.importedQuantity} formatter={(v) => formatNumberVi(Number(v), 2)} />
                        </Col>
                        <Col xs={24} md={6}>
                          <Statistic title="SL lấy" value={selectedInventoryRow.consumedQuantity} formatter={(v) => formatNumberVi(Number(v), 2)} />
                        </Col>
                        <Col xs={24} md={6}>
                          <Statistic title="Tồn ước tính" value={selectedInventoryRow.stockQuantity} formatter={(v) => formatNumberVi(Number(v), 2)} />
                        </Col>
                        <Col xs={24} md={6}>
                          <Statistic title="Giá trị tồn" value={selectedInventoryRow.estimatedStockAmount} formatter={(v) => formatNumberVi(Number(v), 0)} />
                        </Col>
                      </Row>

                      <Card size="small" title="Chi tiết SL nhập" style={{ marginBottom: 12 }}>
                        <Table
                          rowKey="key"
                          columns={inventoryImportDetailColumns}
                          dataSource={selectedInventoryRow.importDetails}
                          pagination={{ pageSize: 8 }}
                          scroll={{ x: 1050, y: 240 }}
                          locale={{ emptyText: 'Không có chi tiết phiếu nhập cho dòng này.' }}
                        />
                      </Card>

                      <Card size="small" title="Chi tiết SL lấy">
                        <Table
                          rowKey="key"
                          columns={inventoryConsumptionDetailColumns}
                          dataSource={selectedInventoryRow.consumptionDetails}
                          pagination={{ pageSize: 8 }}
                          scroll={{ x: 1250, y: 280 }}
                          locale={{ emptyText: 'Không có chi tiết trừ kho cho dòng này.' }}
                        />
                      </Card>
                    </>
                  )}
                </Modal>
              </>
            ),
          },
          {
            key: 'partner-coffee-management',
            label: 'Đối tác',
            children: <PartnerCoffeeManagementSection reloadToken={partnerTabReloadToken} />,
          },
        ].filter((tabItem) => {
          if (!Array.isArray(allowedTabKeys) || allowedTabKeys.length === 0) {
            return true;
          }
          return allowedTabKeys.includes(String(tabItem.key));
        })}
      />
    </>
  );

  if (embedded) {
    return tabsContent;
  }

  return <AdminLayout>{tabsContent}</AdminLayout>;
};

export default IngredientManagement;

